// src/pages/planejamento/AgendamentosTecnicosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { addDoc, arrayUnion, collection, doc, getDoc, getDocs, limit, query, serverTimestamp, updateDoc, where, writeBatch } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { NIVEIS } from "../../config/niveis";
import { nivelUsuario } from "../../utils/permissoes";
import { db } from "../../config/firebase";
import { usePlanejamentoAtendimentos } from "../../hooks/usePlanejamentoAtendimentos";
import { useGoogleMapsDistance } from "../../hooks/useGoogleMapsDistance";
import { apiMapsTolls } from "../../services/mapsConfigApi";
import { ativarPreventivasPlanejadasAteHoje } from "../../services/abrirPreventivasClienteApi";

import "./AgendamentosTecnicos.css";

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function startOfWeekMonday(d) {
  const x = startOfDay(d);
  const day = x.getDay();
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
}

function getWeekDays(weekStart) {
  return Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
}

function fmtDate(d) {
  return d.toLocaleDateString("pt-BR");
}

function normalizeContratoTipo(value) {
  const str = String(value || "").trim();
  if (!str) return "-";
  // Mapeamento comum: AVULSO -> "Avulso", MENSAL -> "Mensal", etc.
  const mapping = {
    "avulso": "Avulso",
    "mensal": "Mensal",
    "quinzenal": "Quinzenal",
    "semanal": "Semanal",
  };
  return mapping[str.toLowerCase()] || str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const PERIODICIDADE_ORDEM = ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"];

function normalizarPeriodicidade(value) {
  const v = String(value || "").trim().toUpperCase();
  return PERIODICIDADE_ORDEM.includes(v) ? v : "";
}

function escolherMaiorPeriodicidade(lista) {
  let maxIdx = -1;
  (Array.isArray(lista) ? lista : []).forEach((item) => {
    const per = normalizarPeriodicidade(item);
    const idx = PERIODICIDADE_ORDEM.indexOf(per);
    if (idx > maxIdx) maxIdx = idx;
  });
  return maxIdx >= 0 ? PERIODICIDADE_ORDEM[maxIdx] : "";
}

function isContratoAtivo(statusContrato) {
  return String(statusContrato || "").trim().toUpperCase() === "APROVADO";
}

function isCorretiva(tipoServico) {
  const tipo = String(tipoServico || "").toLowerCase();
  if (!tipo) return true;
  if (tipo.includes("instal")) return false;
  return true;
}

function monthString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function toDateTimeInput(value) {
  if (!value) return "";
  const dt = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function nowDateTimeLocal() {
  return toDateTimeInput(new Date());
}

function formatDateTime(value) {
  if (!value) return "-";
  const dt = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function inferirQuinzenaDaCompetencia(competencia) {
  const comp = String(competencia || "").trim().toUpperCase();
  if (comp.endsWith("-Q1")) return "Q1";
  if (comp.endsWith("-Q2")) return "Q2";
  return null;
}

const STATUS_ATENDIMENTO = [
  { value: "", label: "Todos" },
  { value: "ABERTO", label: "Aberto" },
  { value: "EM_EXECUCAO", label: "Em execucao" },
  { value: "CONCLUIDO", label: "Concluido" },
];

const STATUS_SESSAO = [
  { value: "PLANEJADA", label: "Planejada" },
  { value: "AGENDADA", label: "Agendada" },
  { value: "ATENDIDA", label: "Atendida" },
  { value: "FINALIZADA", label: "Finalizada" },
  { value: "CANCELADA", label: "Cancelada" },
];

export default function AgendamentosTecnicosPage({
  titulo = "Agendamentos (Técnicos)",
  descricao = "Defina técnico e datas das sessões por semana.",
}) {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const isMobile = useIsMobile(900);
  const navigate = useNavigate();
  const grupoId = usuario?.grupoId || "";

  const {
    scope,
    listarAtendimentosPorClienteCompetencia,
    listarSessoesDoAtendimento,
    listarSessoesPorTecnicoPeriodo,
    listarSessoesPorPeriodo,
    atualizarSessao,
    listarItensSessao,
    listarItensSessaoResumo,
    contarItensSessao,
    excluirAtendimento,
  } = usePlanejamentoAtendimentos();

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [tecnicos, setTecnicos] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [tecnicoId, setTecnicoId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [competenciaMes, setCompetenciaMes] = useState(monthString());
  const [quinzena, setQuinzena] = useState("TODAS");
  const [statusFiltro, setStatusFiltro] = useState("");

  const [atendimentos, setAtendimentos] = useState([]);
  const [atendimentosLoading, setAtendimentosLoading] = useState(false);
  const [sessaoCounts, setSessaoCounts] = useState({});
  const [atendimentosInfo, setAtendimentosInfo] = useState({}); // { [atendId]: { academiaId, academiaNome, osCount, preventivaCount } }

  const [sessoesSemana, setSessoesSemana] = useState([]);
  const [todasAsSessoes, setTodasAsSessoes] = useState([]); // Todas as sessões (para filtrar visitas agendadas)
  const [sessoesLoading, setSessoesLoading] = useState(false);
  const [sessaoItensCount, setSessaoItensCount] = useState({});
  const [sessaoItensTipoCount, setSessaoItensTipoCount] = useState({});
  const [periodicidadeAtendimento, setPeriodicidadeAtendimento] = useState({});
  const [instalacaoAtendimento, setInstalacaoAtendimento] = useState({});
  const [pendenciaNaoPreventivaAtendimento, setPendenciaNaoPreventivaAtendimento] = useState({});
  const [pendentes, setPendentes] = useState([]);
  const [pendentesLoading, setPendentesLoading] = useState(false);
  const [pendentesItensCount, setPendentesItensCount] = useState({});
  const [emergencialByAtendimento, setEmergencialByAtendimento] = useState({});

  const [atendimentoSelecionado, setAtendimentoSelecionado] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  // "assign" = agendar (definir tecnico/data), "edit" = editar sessão já agendada
  const [modalMode, setModalMode] = useState("assign");
  const [modalSessaoId, setModalSessaoId] = useState(null);
  const [modalAtendimento, setModalAtendimento] = useState(null);
  const [modalEmergencialContrato, setModalEmergencialContrato] = useState(false);
  const [modalTecnicoId, setModalTecnicoId] = useState("");
  const [modalTecnicosAcompanhantesIds, setModalTecnicosAcompanhantesIds] = useState([]);
  const [modalAgendaBloqueada, setModalAgendaBloqueada] = useState(false);
  const [modalMotivoBloqueio, setModalMotivoBloqueio] = useState("");
  const [modalMotivoRemanejamento, setModalMotivoRemanejamento] = useState("");
  const [modalOriginalAgendamento, setModalOriginalAgendamento] = useState({ inicio: "", fim: "", tecnicoId: "" });

  // Agora sempre abre o modal diretamente com a primeira visita (sem seletor intermediário)
  const modalIsContrato = useMemo(() => {
    const t = String(modalAtendimento?.contratoTipo || "").trim().toUpperCase();
    if (modalEmergencialContrato) return true;
    return !!t && t !== "AVULSO";
  }, [modalAtendimento?.contratoTipo, modalEmergencialContrato]);
  const [modalInicio, setModalInicio] = useState("");
  const [modalFim, setModalFim] = useState("");
  const [modalStatus, setModalStatus] = useState("PLANEJADA");
  const [modalObs, setModalObs] = useState("");
  const [modalKmRodados, setModalKmRodados] = useState("");
  const [modalKmIda, setModalKmIda] = useState(0);
  const [modalKmVolta, setModalKmVolta] = useState(0);
  const [modalValorDeslocamento, setModalValorDeslocamento] = useState("");
  const [modalValorPedagio, setModalValorPedagio] = useState("");
  const [modalPedagioIda, setModalPedagioIda] = useState(0);
  const [modalPedagioVolta, setModalPedagioVolta] = useState(0);
  const [modalValorMaoObra, setModalValorMaoObra] = useState("");
  const [modalValorPecas, setModalValorPecas] = useState("");
  const [modalValorDesconto, setModalValorDesconto] = useState("");
  const [modalValorTotal, setModalValorTotal] = useState("");
  const [modalEnderecoDestino, setModalEnderecoDestino] = useState("");
  const [modalDestinoCoords, setModalDestinoCoords] = useState(null);
  const [modalCalculandoDistancia, setModalCalculandoDistancia] = useState(false);
	const [modalDistanciaCalculada, setModalDistanciaCalculada] = useState(false);
  const [salvandoSessao, setSalvandoSessao] = useState(false);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [resumoOpen, setResumoOpen] = useState(false);
  const [resumoSessao, setResumoSessao] = useState(null);
  const [resumoItens, setResumoItens] = useState([]);
  const [resumoTotal, setResumoTotal] = useState(0);
  const [resumoLoading, setResumoLoading] = useState(false);

  const { calcularDistancia, gerarUrlGoogleMaps, loading: loadingDistancia } = useGoogleMapsDistance();
  const [enderecoOrigem, setEnderecoOrigem] = useState("");
  const [valorKmDeslocamento, setValorKmDeslocamento] = useState(1.2);
  const [modalIdaVolta, setModalIdaVolta] = useState(true);

  // Data selecionada para mostrar a semana começando a partir dela (não mais fixa em segunda)
  const [dataVizualização, setDataVizualizacao] = useState(startOfDay(new Date()));

  const weekStart = useMemo(() => dataVizualização, [dataVizualização]);
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const sessoesPorDia = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => {
      map[d.toISOString().slice(0, 10)] = [];
    });
    sessoesSemana.forEach((s) => {
      if (!s.inicioEm) return;
      const dt = typeof s.inicioEm?.toDate === "function" ? s.inicioEm.toDate() : new Date(s.inicioEm);
      if (Number.isNaN(dt.getTime())) return;
      const key = startOfDay(dt).toISOString().slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const da = a.inicioEm?.toDate ? a.inicioEm.toDate().getTime() : new Date(a.inicioEm).getTime();
        const dbv = b.inicioEm?.toDate ? b.inicioEm.toDate().getTime() : new Date(b.inicioEm).getTime();
        return da - dbv;
      });
    });
    return map;
  }, [sessoesSemana, weekDays]);

  const pendentesSemHorario = useMemo(
    () => pendentes.filter((s) => !s.inicioEm),
    [pendentes]
  );

  const pendenciasPorAtendimento = useMemo(() => {
    const map = {};
    (pendentes || []).forEach((sessao) => {
      const atendimentoId = String(sessao?.atendimentoId || "").trim();
      if (!atendimentoId) return;
      if (!Array.isArray(map[atendimentoId])) map[atendimentoId] = [];
      map[atendimentoId].push(sessao);
    });
    return map;
  }, [pendentes]);

  const isGlobalTecnico = tecnicoId === "__GLOBAL__";

  const sessoesVisiveisPorAtendimento = useMemo(() => {
    const map = {};
    const sessoesMap = new Map();

    [...(todasAsSessoes || []), ...(pendentes || [])].forEach((sessao) => {
      const sessaoId = String(sessao?.id || "").trim();
      if (!sessaoId) return;
      if (!sessoesMap.has(sessaoId)) {
        sessoesMap.set(sessaoId, sessao);
      }
    });

    Array.from(sessoesMap.values()).forEach((sessao) => {
      const atendimentoId = String(sessao?.atendimentoId || "").trim();
      if (!atendimentoId) return;
      if (!Array.isArray(map[atendimentoId])) map[atendimentoId] = [];
      map[atendimentoId].push(sessao);
    });

    return map;
  }, [todasAsSessoes, pendentes]);

  const leftPanelAtendimentos = useMemo(() => {
    return (atendimentos || [])
      .filter((a) => {
        const sessoesDoAtendimento = sessoesVisiveisPorAtendimento[String(a?.id || "")] || [];
        if (!sessoesDoAtendimento.length) return false;

        if (isGlobalTecnico || !tecnicoId) return true;

        return sessoesDoAtendimento.some((sessao) => {
          const sessaoTecnicoId = String(sessao?.tecnicoId || "").trim();
          if (!sessaoTecnicoId) return true;
          if (sessaoTecnicoId === String(tecnicoId || "").trim()) return false;
          const statusSessao = String(sessao?.status || "").toUpperCase();
          if (statusSessao === "FINALIZADA" || statusSessao === "CANCELADA") return false;
          const acompanhantesIds = Array.isArray(sessao?.tecnicosAcompanhantesIds)
            ? sessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim())
            : [];
          return !acompanhantesIds.includes(String(tecnicoId || "").trim());
        });
      })
      .filter((a) => {
        const contratoTipo = String(a?.contratoTipo || "").toUpperCase();
        const isAvulso = contratoTipo.includes("AVULSO");

        if (!isAvulso) return true;

        const sessoesDoAtendimento = sessoesVisiveisPorAtendimento[String(a?.id || "")] || [];
        const temPropostaAprovada = sessoesDoAtendimento.some(
          (sessaoItem) => String(sessaoItem?.propostaStatus || "").toUpperCase() === "APROVADA" || sessaoItem?.liberadaAgendamento === true
        );
        const emergencialDisponivel = emergencialByAtendimento[a.id]?.disponivel === true;

        return temPropostaAprovada || emergencialDisponivel;
      });
  }, [atendimentos, sessoesVisiveisPorAtendimento, tecnicoId, isGlobalTecnico, emergencialByAtendimento]);

  const parseNumero = (value) => {
    const raw = String(value || "").replace(",", ".");
    const num = Number.parseFloat(raw);
    return Number.isNaN(num) ? 0 : num;
  };

  const formatNumero = (value, decimals = 2) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    return num.toFixed(decimals);
  };

  const toggleTecnicoAcompanhante = (id) => {
    setModalTecnicosAcompanhantesIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const modalTemRemanejamento = useMemo(() => {
    if (modalMode !== "edit") return false;
    return (
      String(modalOriginalAgendamento?.inicio || "") !== String(modalInicio || "") ||
      String(modalOriginalAgendamento?.fim || "") !== String(modalFim || "") ||
      String(modalOriginalAgendamento?.tecnicoId || "") !== String(modalTecnicoId || "")
    );
  }, [
    modalMode,
    modalOriginalAgendamento?.inicio,
    modalOriginalAgendamento?.fim,
    modalOriginalAgendamento?.tecnicoId,
    modalInicio,
    modalFim,
    modalTecnicoId,
  ]);

  // Recalcular km rodado quando ida/volta muda
  useEffect(() => {
    if (modalKmIda > 0) {
      const totalKm = modalIdaVolta ? modalKmIda + modalKmVolta : modalKmIda;
      setModalKmRodados(totalKm.toString());
    }
  }, [modalIdaVolta, modalKmIda, modalKmVolta]);

  // Recalcular pedagio total quando ida/volta muda
  useEffect(() => {
    const totalToll = modalIdaVolta 
      ? parseNumero(modalPedagioIda) + parseNumero(modalPedagioVolta)
      : parseNumero(modalPedagioIda);
    setModalValorPedagio(formatNumero(totalToll));
  }, [modalIdaVolta, modalPedagioIda, modalPedagioVolta]);

  // Recalcular valores totais
  useEffect(() => {
    if (modalIsContrato) {
      setModalValorDeslocamento(formatNumero(0));
      setModalValorTotal(formatNumero(0));
      return;
    }

    const km = parseNumero(modalKmRodados);
    const mao = parseNumero(modalValorMaoObra);
    const pecas = parseNumero(modalValorPecas);
    const pedagio = parseNumero(modalValorPedagio);
    const desconto = parseNumero(modalValorDesconto);

    // modalKmRodados já contém o total correto (ida + volta se for ida e volta)
    const desloc = km * valorKmDeslocamento;
    const total = mao + pecas + desloc + pedagio - desconto;

    setModalValorDeslocamento(formatNumero(desloc));
    setModalValorTotal(formatNumero(total));
  }, [modalKmRodados, modalValorMaoObra, modalValorPecas, modalValorPedagio, modalValorDesconto, valorKmDeslocamento, modalIdaVolta, modalIsContrato]);

  useEffect(() => {
    const loadClientes = async () => {
      if (!usuario?.grupoId) return;
      const q = query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId));
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
      setClientes(lista);
    };
    loadClientes();
  }, [usuario?.grupoId]);

  // Carregar endereço de origem e valor por km do grupo
  useEffect(() => {
    const loadEnderecoOrigem = async () => {
      if (!usuario?.grupoId) return;
      try {
        const grupoRef = doc(db, "grupos", usuario.grupoId);
        const grupoSnap = await getDoc(grupoRef);
        if (grupoSnap.exists()) {
          const grupoData = grupoSnap.data();
          setEnderecoOrigem(grupoData.enderecoOrigem || "");
          if (grupoData.valorKmDeslocamento != null) {
            const parsed = Number.parseFloat(String(grupoData.valorKmDeslocamento));
            if (!Number.isNaN(parsed)) setValorKmDeslocamento(parsed);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar endereço do grupo:", err);
      }
    };
    loadEnderecoOrigem();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const loadTecnicos = async () => {
      if (!usuario?.grupoId) return;
      const q = query(
        collection(db, "usuarios"),
        where("grupoId", "==", usuario.grupoId)
      );
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => nivelUsuario(u) >= NIVEIS.TECNICO)
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
      setTecnicos(lista);
      if (!tecnicoId && lista.length) {
        setTecnicoId(lista[0].id);
      }
    };
    loadTecnicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId]);

  useEffect(() => {
    if (!modoGlobal && empresaAtual?.id && !clienteId) {
      setClienteId(empresaAtual.id);
    }
  }, [modoGlobal, empresaAtual?.id, clienteId]);

  const filtrarAtendimentos = (lista, manterTodosMesesMap = pendenciaNaoPreventivaAtendimento) => {
    let base = [...lista];
    if (competenciaMes) {
      const manterTodosMeses = base.filter((a) => !!manterTodosMesesMap[String(a?.id || "")]);
      let somenteMensais = base.filter((a) => !manterTodosMesesMap[String(a?.id || "")]);

      if (quinzena === "Q1" || quinzena === "Q2") {
        const competenciaFiltro = `${competenciaMes}-${quinzena}`;
        somenteMensais = somenteMensais.filter(
          (a) => String(a.competencia || "").toUpperCase() === String(competenciaFiltro || "").toUpperCase()
        );
      } else {
        somenteMensais = somenteMensais.filter((a) => String(a.competencia || "").startsWith(competenciaMes));

        // Evita duplicata legada: quando já existem Q1/Q2 no mês para o mesmo cliente+setor,
        // não exibe a competência mensal "YYYY-MM" desse mesmo par.
        const chavesComQuinzena = new Set(
          somenteMensais
            .filter((a) => /-Q[12]$/i.test(String(a?.competencia || "")))
            .map((a) => {
              const cliente = String(a?.clienteId || "").trim();
              const setor = String(a?.setorId || a?.academiaId || "").trim();
              return `${cliente}|${setor}`;
            })
        );

        somenteMensais = somenteMensais.filter((a) => {
          const comp = String(a?.competencia || "").trim();
          if (!/^\d{4}-\d{2}$/i.test(comp)) return true;
          const cliente = String(a?.clienteId || "").trim();
          const setor = String(a?.setorId || a?.academiaId || "").trim();
          const chave = `${cliente}|${setor}`;
          return !chavesComQuinzena.has(chave);
        });
      }

      base = [...somenteMensais, ...manterTodosMeses];
    }
    if (statusFiltro) {
      base = base.filter((a) => String(a.status || "") === statusFiltro);
    }
    return base;
  };

  const canGlobalView = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return (
      usuario?.acessoGlobal === true
      || usuario?.globalAccess === true
      || usuario?.tipoAcesso === "GLOBAL"
      || nivel >= NIVEIS.ADMIN
    );
  }, [usuario]);
  const canGerenciarBloqueio = useMemo(() => nivelUsuario(usuario) >= NIVEIS.ADMIN, [usuario]);

  const sincronizarDataECompetencia = (novaData) => {
    const dia = startOfDay(novaData);
    setDataVizualizacao(dia);
    setCompetenciaMes(monthString(dia));
  };

  // Navegação de datas para visualização
  const irParaSemanaAnterior = () => {
    sincronizarDataECompetencia(addDays(dataVizualização, -7));
  };

  const irParaSemanaSeguinte = () => {
    sincronizarDataECompetencia(addDays(dataVizualização, 7));
  };

  const irParaHoje = () => {
    sincronizarDataECompetencia(new Date());
  };

  const navegarMesAgenda = (deltaMes) => {
    const next = new Date(dataVizualização);
    next.setMonth(next.getMonth() + deltaMes);
    sincronizarDataECompetencia(next);
  };

  const irParaPróximoMês = () => {
    navegarMesAgenda(1);
  };

  const irParaMêsAnterior = () => {
    navegarMesAgenda(-1);
  };

  useEffect(() => {
    const m = String(competenciaMes || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return;

    const ano = Number(m[1]);
    const mesIdx = Number(m[2]) - 1;
    const primeiroDiaMes = startOfDay(new Date(ano, mesIdx, 1));
    setDataVizualizacao(primeiroDiaMes);
  }, [competenciaMes]);

  const abrirNovaVisitaDoAtendimento = (atendimentoRef) => {
    if (!atendimentoRef?.clienteId) {
      setErro("Selecione um atendimento para criar a visita.");
      return;
    }

    const comp = String(atendimentoRef.competencia || "");
    const mes = comp ? comp.slice(0, 7) : "";
    const q = comp.includes("-Q2") ? "Q2" : (comp.includes("-Q1") ? "Q1" : "");

    const qs = new URLSearchParams();
    qs.set("clienteId", String(atendimentoRef.clienteId));
    if (atendimentoRef.contratoTipo) qs.set("contratoTipo", String(atendimentoRef.contratoTipo));
    if (mes) qs.set("competenciaMes", mes);
    if (q) qs.set("quinzena", q);
    const acad = atendimentoRef.academiaId || atendimentoRef.setorId || "";
    if (acad) qs.set("academiaId", String(acad));
    qs.set("returnTo", "/planejamento/calendario");

    navigate(`/planejamento/nova-visita?${qs.toString()}`);
  };

  const clientesById = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [clientes]);

  const tecnicosById = useMemo(() => {
    const map = {};
    tecnicos.forEach((t) => {
      map[String(t.id)] = t;
    });
    return map;
  }, [tecnicos]);

  const atendimentosById = useMemo(() => {
    const map = {};
    atendimentos.forEach((a) => {
      map[String(a.id)] = a;
    });
    return map;
  }, [atendimentos]);

  const carregarAtendimentos = async () => {
    const isGlobalCliente = clienteId === "__GLOBAL__";
    const allowGlobal = canGlobalView && (!clienteId || isGlobalCliente);
    if ((!clienteId || isGlobalCliente) && !allowGlobal) {
      setAtendimentos([]);
      return;
    }
    const empresaIdValue = scope?.empresaId || (isGlobalCliente ? null : clienteId) || null;
    if (!scope?.grupoId || (!empresaIdValue && !allowGlobal)) {
      setErro("Selecione uma empresa para carregar atendimentos.");
      return;
    }
    setErro("");
    setAtendimentosLoading(true);
    try {
      if (!isGlobalCliente && clienteId && scope?.grupoId) {
        await ativarPreventivasPlanejadasAteHoje({
          grupoId: scope.grupoId,
          empresaId: clienteId,
          referencia: new Date(),
        });
      }

      let lista = await listarAtendimentosPorClienteCompetencia(
        isGlobalCliente ? null : (clienteId || null),
        "",
        {
          empresaId: empresaIdValue || undefined,
          allowGlobal,
        }
      );

      const garantirCombosPreventivasCompetencia = async (listaBase) => {
        if (isGlobalCliente || !clienteId || !competenciaMes || !scope?.grupoId) return false;

        const competenciasAlvo = [];
        if (quinzena === "Q1" || quinzena === "Q2") {
          competenciasAlvo.push(`${competenciaMes}-${quinzena}`);
        } else {
          competenciasAlvo.push(competenciaMes);
        }

        const snapPrev = await getDocs(
          query(
            collection(db, "preventivas"),
            where("grupoId", "==", scope.grupoId),
            where("empresaId", "==", clienteId),
            limit(1000)
          )
        );

        const preventivas = snapPrev.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p?.ativo !== false)
          .filter((p) => !!String(p?.setorId || "").trim());

        const preventivasPlanejadas = preventivas.filter((p) => p?.planejadaPorContrato === true);
        const preventivasBase = preventivas.filter((p) => p?.planejadaPorContrato !== true);

        if (!preventivas.length) return false;

        const atendimentoByKey = new Map();
        (listaBase || []).forEach((a) => {
          const comp = String(a?.competencia || "").trim();
          const setor = String(a?.setorId || a?.academiaId || "").trim();
          if (comp && setor) atendimentoByKey.set(`${comp}|${setor}`, a);
        });

        let alterou = false;

        for (const competencia of competenciasAlvo) {
          const isQ = competencia.endsWith("-Q1") || competencia.endsWith("-Q2");
          const quinzenaComp = competencia.endsWith("-Q2") ? "Q2" : (competencia.endsWith("-Q1") ? "Q1" : null);
          const competenciaMesComp = isQ ? competencia.slice(0, 7) : competencia.slice(0, 7);

          const planejadasDaCompetencia = preventivasPlanejadas.filter(
            (p) => String(p?.competencia || "").trim() === competencia
          );

          const preventivasAlvo = planejadasDaCompetencia.length > 0
            ? planejadasDaCompetencia
            : preventivasBase.map((p) => ({ ...p, slotKey: `${p.id}|${competencia}` }));

          const porSetor = new Map();
          preventivasAlvo.forEach((p) => {
            const setor = String(p?.setorId || "").trim();
            if (!setor) return;
            if (!porSetor.has(setor)) porSetor.set(setor, []);
            porSetor.get(setor).push(p);
          });

          for (const [setorId, prevsSetor] of porSetor.entries()) {
            const keyAt = `${competencia}|${setorId}`;
            let atendimento = atendimentoByKey.get(keyAt) || null;

            if (!atendimento?.id) {
              const refAt = await addDoc(collection(db, "atendimentos_preventiva"), {
                grupoId: scope.grupoId,
                empresaId: clienteId,
                clienteId,
                contratoTipo: "Mensal",
                competencia,
                status: "ABERTO",
                startedAt: null,
                progresso: null,
                setorId,
                academiaId: setorId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              atendimento = {
                id: refAt.id,
                grupoId: scope.grupoId,
                empresaId: clienteId,
                clienteId,
                competencia,
                setorId,
                academiaId: setorId,
                contratoTipo: "Mensal",
                status: "ABERTO",
              };
              atendimentoByKey.set(keyAt, atendimento);
              alterou = true;
            }

            const snapSess = await getDocs(
              query(
                collection(db, "atendimentos_sessoes"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", clienteId),
                where("atendimentoId", "==", atendimento.id),
                limit(1)
              )
            );

            let sessaoId = null;
            if (!snapSess.empty) {
              sessaoId = snapSess.docs[0].id;
            } else {
              const refSess = await addDoc(collection(db, "atendimentos_sessoes"), {
                grupoId: scope.grupoId,
                empresaId: clienteId,
                atendimentoId: atendimento.id,
                tecnicoId: null,
                inicioEm: null,
                fimEm: null,
                status: "PLANEJADA",
                observacoes: null,
                propostaStatus: "APROVADA",
                liberadaAgendamento: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              sessaoId = refSess.id;
              alterou = true;
            }

            if (!sessaoId) continue;

            const snapItens = await getDocs(
              query(
                collection(db, "atendimentos_sessao_itens"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", clienteId),
                where("sessaoId", "==", sessaoId),
                where("tipo", "==", "PREVENTIVA")
              )
            );
            const slots = new Set(
              snapItens.docs
                .map((d) => String(d.data()?.slotKey || "").trim())
                .filter(Boolean)
            );

            const batch = writeBatch(db);
            let batchCount = 0;
            prevsSetor.forEach((p) => {
              const slotKey = String(p?.slotKey || `${p?.origemPreventivaId || p?.id}|${competencia}`).trim();
              if (slots.has(slotKey)) return;
              const refItem = doc(collection(db, "atendimentos_sessao_itens"));
              batch.set(refItem, {
                grupoId: scope.grupoId,
                empresaId: clienteId,
                atendimentoId: atendimento.id,
                sessaoId,
                clienteId,
                tipo: "PREVENTIVA",
                preventivaId: p.id,
                preventivaCodigo: p?.codigo || p?.modeloPreventivaCodigo || null,
                preventivaNome: p?.nome || null,
                maquinaId: p?.maquinaId || null,
                maquinaNome: p?.maquinaNome || null,
                periodicidade: p?.periodicidade || null,
                setorId,
                setorNome: p?.setorNome || null,
                competencia,
                competenciaMes: competenciaMesComp,
                quinzena: quinzenaComp,
                slotKey,
                createdAt: serverTimestamp(),
              });
              batchCount += 1;
            });

            if (batchCount > 0) {
              await batch.commit();
              alterou = true;
            }
          }
        }

        return alterou;
      };

      const garantirVisitasInstalacaoAprovadas = async (listaBase) => {
        if (!scope?.grupoId) return false;

        let snapPropostas;
        if (isGlobalCliente) {
          snapPropostas = await getDocs(
            query(
              collection(db, "financeiro_propostas_instalacoes"),
              where("grupoId", "==", scope.grupoId),
              where("status", "==", "APROVADA")
            )
          );
        } else {
          if (!clienteId) return false;
          snapPropostas = await getDocs(
            query(
              collection(db, "financeiro_propostas_instalacoes"),
              where("grupoId", "==", scope.grupoId),
              where("clienteId", "==", clienteId),
              where("status", "==", "APROVADA")
            )
          );
        }

        const propostas = snapPropostas.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!propostas.length) return false;

        const atendimentoById = new Map((listaBase || []).map((a) => [String(a.id), a]));
        let alterou = false;

        for (const proposta of propostas) {
          const propostaId = String(proposta?.id || "").trim();
          const clienteRef = String(proposta?.clienteId || "").trim();
          const academiaRef = String(proposta?.academiaId || proposta?.setorId || "").trim();
          if (!propostaId || !clienteRef || !academiaRef) continue;

          const competenciaProposta = String(
            proposta?.competencia ||
            ((quinzena === "Q1" || quinzena === "Q2") ? `${competenciaMes}-${quinzena}` : competenciaMes) ||
            monthString()
          ).trim();

          let atendimentoIdRef = String(proposta?.atendimentoId || "").trim();
          let atendimento = atendimentoIdRef ? (atendimentoById.get(atendimentoIdRef) || null) : null;

          if (!atendimento && atendimentoIdRef) {
            try {
              const snapAt = await getDoc(doc(db, "atendimentos_preventiva", atendimentoIdRef));
              if (snapAt.exists()) {
                atendimento = { id: snapAt.id, ...snapAt.data() };
                atendimentoById.set(String(atendimento.id), atendimento);
              }
            } catch {
              atendimento = null;
            }
          }

          if (!atendimento) {
            const refAt = await addDoc(collection(db, "atendimentos_preventiva"), {
              grupoId: scope.grupoId,
              empresaId: clienteRef,
              clienteId: clienteRef,
              contratoTipo: "Avulso",
              competencia: competenciaProposta,
              status: "ABERTO",
              startedAt: null,
              progresso: null,
              setorId: academiaRef,
              academiaId: academiaRef,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });

            atendimento = {
              id: refAt.id,
              grupoId: scope.grupoId,
              empresaId: clienteRef,
              clienteId: clienteRef,
              contratoTipo: "Avulso",
              competencia: competenciaProposta,
              status: "ABERTO",
              setorId: academiaRef,
              academiaId: academiaRef,
            };
            atendimentoIdRef = refAt.id;
            atendimentoById.set(String(atendimento.id), atendimento);
            alterou = true;
          }

          let sessaoIdRef = String(proposta?.sessaoId || "").trim();
          let sessaoExiste = false;
          if (sessaoIdRef) {
            try {
              const snapSessao = await getDoc(doc(db, "atendimentos_sessoes", sessaoIdRef));
              sessaoExiste = snapSessao.exists();
            } catch {
              sessaoExiste = false;
            }
          }

          if (!sessaoExiste) {
            const snapSess = await getDocs(
              query(
                collection(db, "atendimentos_sessoes"),
                where("grupoId", "==", scope.grupoId),
                where("atendimentoId", "==", atendimentoIdRef),
                limit(1)
              )
            );

            if (!snapSess.empty) {
              sessaoIdRef = snapSess.docs[0].id;
            } else {
              const refSess = await addDoc(collection(db, "atendimentos_sessoes"), {
                grupoId: scope.grupoId,
                empresaId: clienteRef,
                atendimentoId: atendimentoIdRef,
                tecnicoId: null,
                inicioEm: null,
                fimEm: null,
                status: "PLANEJADA",
                observacoes: null,
                propostaStatus: "APROVADA",
                liberadaAgendamento: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              sessaoIdRef = refSess.id;
              alterou = true;
            }
          }

          if (sessaoIdRef) {
            await updateDoc(doc(db, "atendimentos_sessoes", sessaoIdRef), {
              propostaStatus: "APROVADA",
              liberadaAgendamento: true,
              updatedAt: serverTimestamp(),
            });
          }

          let possuiItemInstalacao = false;
          if (sessaoIdRef) {
            const snapItens = await getDocs(
              query(
                collection(db, "atendimentos_sessao_itens"),
                where("grupoId", "==", scope.grupoId),
                where("sessaoId", "==", sessaoIdRef)
              )
            );
            possuiItemInstalacao = snapItens.docs.some((d) => {
              const item = d.data() || {};
              const tipo = String(item?.tipo || "").toUpperCase();
              return (
                tipo === "INSTALACAO" ||
                String(item?.propostaInstalacaoId || "").trim() === propostaId
              );
            });

            if (!possuiItemInstalacao) {
              await addDoc(collection(db, "atendimentos_sessao_itens"), {
                grupoId: scope.grupoId,
                empresaId: clienteRef,
                atendimentoId: atendimentoIdRef,
                sessaoId: sessaoIdRef,
                clienteId: clienteRef,
                tipo: "INSTALACAO",
                equipamentoId: propostaId,
                equipamentoNome: proposta?.academiaNome || proposta?.clienteNome || `Instalação ${propostaId.slice(0, 6)}`,
                checklistInstalacaoId: null,
                checklistInstalacaoNome: null,
                propostaInstalacaoId: propostaId,
                propostaInstalacaoNome: proposta?.academiaNome || proposta?.clienteNome || "Instalação",
                setorId: academiaRef,
                setorNome: proposta?.academiaNome || null,
                competencia: atendimento?.competencia || competenciaProposta,
                competenciaMes: String((atendimento?.competencia || competenciaProposta) || "").slice(0, 7) || null,
                quinzena: (quinzena === "Q1" || quinzena === "Q2") ? quinzena : null,
                createdAt: serverTimestamp(),
              });
              alterou = true;
            }
          }

          const atendimentoMudou = String(proposta?.atendimentoId || "").trim() !== String(atendimentoIdRef || "").trim();
          const sessaoMudou = String(proposta?.sessaoId || "").trim() !== String(sessaoIdRef || "").trim();

          if (atendimentoMudou || sessaoMudou) {
            await updateDoc(doc(db, "financeiro_propostas_instalacoes", propostaId), {
              atendimentoId: atendimentoIdRef || null,
              sessaoId: sessaoIdRef || null,
              updatedAt: serverTimestamp(),
            });
            alterou = true;
          }
        }

        return alterou;
      };

      const alterouPlanejamento = await garantirCombosPreventivasCompetencia(lista);
      const alterouInstalacoes = await garantirVisitasInstalacaoAprovadas(lista);
      if (alterouPlanejamento || alterouInstalacoes) {
        lista = await listarAtendimentosPorClienteCompetencia(
          isGlobalCliente ? null : (clienteId || null),
          "",
          {
            empresaId: empresaIdValue || undefined,
            allowGlobal,
          }
        );
      }

      const manterTodosMesesMap = {};
      await Promise.all(
        (lista || []).map(async (a) => {
          try {
            const empresaIdValue = a.empresaId || scope?.empresaId || a.clienteId || null;
            if (!empresaIdValue) return;
            const sessoesAt = await listarSessoesDoAtendimento(a.id, { empresaId: empresaIdValue });
            for (const s of sessoesAt) {
              const semTecnico = !s.tecnicoId;
              const semInicio = !s.inicioEm;
              const statusPend = String(s.status || "").toUpperCase() === "PENDENTE";
              const pendenteAgendamento = semTecnico || semInicio || statusPend;
              if (!pendenteAgendamento) continue;

              const itensSessao = await listarItensSessao(s.id, { empresaId: empresaIdValue });
              const temNaoPreventiva = (Array.isArray(itensSessao) ? itensSessao : []).some((item) => {
                const tipo = String(item?.tipo || "").toUpperCase();
                const isInstalacao = tipo === "INSTALACAO" || !!item?.propostaInstalacaoId || !!item?.checklistInstalacaoId;
                const isOs = tipo === "OS" || !!item?.osId;
                return isInstalacao || isOs;
              });

              if (temNaoPreventiva) {
                manterTodosMesesMap[String(a.id)] = true;
                break;
              }
            }
          } catch {
            // ignora e segue
          }
        })
      );

      setPendenciaNaoPreventivaAtendimento(manterTodosMesesMap);

      const filtrados = filtrarAtendimentos(lista, manterTodosMesesMap);
      setAtendimentos(filtrados);
    } catch (e) {
      console.error("Erro ao carregar atendimentos:", e);
      setErro("Erro ao carregar atendimentos.");
      setAtendimentos([]);
      setPendenciaNaoPreventivaAtendimento({});
    } finally {
      setAtendimentosLoading(false);
    }
  };

  const carregarContagemSessoes = async (lista) => {
    const counts = {};
    await Promise.all(
      lista.map(async (a) => {
        try {
          const empresaIdValue = a.empresaId || scope?.empresaId || a.clienteId || null;
          if (!empresaIdValue) {
            counts[a.id] = 0;
            return;
          }
          const sess = await listarSessoesDoAtendimento(a.id, { empresaId: empresaIdValue });
          counts[a.id] = sess.length;
        } catch {
          counts[a.id] = 0;
        }
      })
    );
    setSessaoCounts(counts);
  };

  const carregarInfoAtendimentos = async (lista) => {
    const info = {};
    await Promise.all(
      lista.map(async (a) => {
        try {
          const empresaIdValue = a.empresaId || scope?.empresaId || a.clienteId || null;
          if (!empresaIdValue) {
            info[a.id] = { academiaId: null, academiaNome: "-", osCount: 0, preventivaCount: 0 };
            return;
          }

          // Obter academia/setor ID do atendimento
          const academiaId = 
            a.setorId || 
            a.academiaId || 
            a.setorID || 
            a.academiaID || 
            a.setor?.id || 
            a.academia?.id || 
            a.progresso?.setorId || 
            a.progresso?.academiaId || 
            null;

          let academiaNome = "-";
          if (academiaId) {
            try {
              const setorRef = doc(db, "setores", academiaId);
              const setorSnap = await getDoc(setorRef);
              if (setorSnap.exists()) {
                academiaNome = setorSnap.data().nome || "-";
              }
            } catch (err) {
              console.error("Erro ao carregar setor:", err);
            }
          }

          // Contar OS e Preventivas nas sessões deste atendimento
          let osCount = 0;
          let preventivaCount = 0;
          
          try {
            const sessoes = await listarSessoesDoAtendimento(a.id, { empresaId: empresaIdValue });
            
            await Promise.all(
              sessoes.map(async (sessao) => {
                try {
                  const itensSnap = await getDocs(
                    query(
                      collection(db, "atendimentos_sessao_itens"),
                      where("grupoId", "==", a.grupoId),
                      where("sessaoId", "==", sessao.id)
                    )
                  );
                  
                  itensSnap.docs.forEach((doc) => {
                    const item = doc.data();
                    if (item.tipo === "OS") osCount++;
                    if (item.tipo === "PREVENTIVA") preventivaCount++;
                  });
                } catch (err) {
                  console.error("Erro ao contar itens da sessão:", err);
                }
              })
            );
          } catch (err) {
            console.error("Erro ao listar sessões para contagem:", err);
          }

          info[a.id] = { academiaId, academiaNome, osCount, preventivaCount };
        } catch (err) {
          console.error("Erro ao carregar info do atendimento:", err);
          info[a.id] = { academiaId: null, academiaNome: "-", osCount: 0, preventivaCount: 0 };
        }
      })
    );
    setAtendimentosInfo(info);
  };

  useEffect(() => {
    carregarAtendimentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, competenciaMes, quinzena, statusFiltro, scope?.grupoId, scope?.empresaId, canGlobalView]);

  useEffect(() => {
    if (atendimentos.length) {
      carregarContagemSessoes(atendimentos);
      carregarInfoAtendimentos(atendimentos);
    } else {
      setSessaoCounts({});
      setAtendimentosInfo({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atendimentos]);

  const carregarSessoesSemana = async () => {
    if (!tecnicoId) {
      setSessoesSemana([]);
      setTodasAsSessoes([]);
      return;
    }
    if (!scope?.grupoId || (!scope?.empresaId && !canGlobalView)) {
      setErro("Selecione uma empresa para carregar sessoes.");
      return;
    }
    setErro("");
    setSessoesLoading(true);
    try {
      const lista = isGlobalTecnico
        ? await listarSessoesPorPeriodo(weekStart, weekEnd)
        : await listarSessoesPorTecnicoPeriodo(tecnicoId, weekStart, weekEnd);
      setSessoesSemana(lista);
      
      // Carregar todas as sessões de todos os técnicos (apenas para filtro de visitas agendadas)
      try {
        const todasSessoes = await listarSessoesPorPeriodo(weekStart, weekEnd);
        setTodasAsSessoes(todasSessoes);
      } catch (e) {
        console.warn("Erro ao carregar todas as sessões para filtro:", e);
        setTodasAsSessoes(lista); // Fallback: usa lista do técnico selecionado
      }
    } catch (e) {
      console.error("Erro ao carregar sessoes:", e);
      setErro("Erro ao carregar sessoes.");
      setSessoesSemana([]);
      setTodasAsSessoes([]);
    } finally {
      setSessoesLoading(false);
    }
  };

  const garantirItensInstalacaoDaSessao = async ({ sessaoId, atendimento }) => {
    const atendimentoId = String(atendimento?.id || "").trim();
    const grupo = String(scope?.grupoId || "").trim();
    const empresa = String(
      atendimento?.empresaId || atendimento?.clienteId || scope?.empresaId || (clienteId && clienteId !== "__GLOBAL__" ? clienteId : "")
    ).trim();
    const clienteRef = String(atendimento?.clienteId || empresa || "").trim();

    if (!sessaoId || !atendimentoId || !grupo || !empresa) return 0;

    const snapItensSessao = await getDocs(
      query(
        collection(db, "atendimentos_sessao_itens"),
        where("grupoId", "==", grupo),
        where("sessaoId", "==", sessaoId)
      )
    );

    const itensSessao = snapItensSessao.docs.map((d) => ({ id: d.id, ...d.data() }));
    const itensInstalacao = itensSessao.filter((item) => {
      const tipo = String(item?.tipo || "").trim().toUpperCase();
      return (
        tipo === "INSTALACAO" ||
        !!item?.propostaInstalacaoId ||
        !!item?.checklistInstalacaoId
      );
    });

    if (!itensInstalacao.length) return 0;

    const propostaIds = new Set(
      itensInstalacao
        .map((item) => String(item?.propostaInstalacaoId || "").trim())
        .filter(Boolean)
    );
    const nomePropostaById = {};
    itensInstalacao.forEach((item) => {
      const propostaId = String(item?.propostaInstalacaoId || "").trim();
      if (!propostaId) return;
      if (!nomePropostaById[propostaId] && item?.propostaInstalacaoNome) {
        nomePropostaById[propostaId] = item.propostaInstalacaoNome;
      }
    });

    const existentesEquipIds = new Set(
      itensInstalacao
        .map((item) => String(item?.equipamentoId || "").trim())
        .filter(Boolean)
    );

    const setorRef = String(atendimento?.setorId || atendimento?.academiaId || "").trim();
    const competencia = String(atendimento?.competencia || "").trim() || null;
    const competenciaMes = competencia ? competencia.slice(0, 7) : null;
    const quinzena = inferirQuinzenaDaCompetencia(competencia);

    const snapMaquinas = await getDocs(
      query(
        collection(db, "maquinas"),
        where("grupoId", "==", grupo),
        where("empresaId", "==", clienteRef)
      )
    );

    const maquinasElegiveis = snapMaquinas.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((maq) => maq?.checklistInstalacaoAtiva === true && !!maq?.checklistInstalacaoId)
      .filter((maq) => {
        if (!setorRef) return true;
        return String(maq?.setorId || "").trim() === setorRef;
      })
      .filter((maq) => {
        if (!propostaIds.size) return true;
        const prop = String(maq?.propostaInstalacaoId || "").trim();
        return !!prop && propostaIds.has(prop);
      })
      .filter((maq) => !existentesEquipIds.has(String(maq?.id || "").trim()));

    if (!maquinasElegiveis.length) return 0;

    const batch = writeBatch(db);
    maquinasElegiveis.forEach((maq) => {
      const propostaId = String(maq?.propostaInstalacaoId || "").trim() || null;
      const propostaNome = propostaId ? (nomePropostaById[propostaId] || null) : null;
      const refItem = doc(collection(db, "atendimentos_sessao_itens"));
      batch.set(refItem, {
        grupoId: grupo,
        empresaId: empresa,
        atendimentoId,
        sessaoId,
        clienteId: clienteRef,
        tipo: "INSTALACAO",
        equipamentoId: maq.id,
        equipamentoNome: maq?.nome || maq?.codigo || maq.id,
        checklistInstalacaoId: maq?.checklistInstalacaoId || null,
        checklistInstalacaoNome: maq?.checklistInstalacaoNome || null,
        propostaInstalacaoId: propostaId,
        propostaInstalacaoNome: propostaNome,
        setorId: maq?.setorId || setorRef || null,
        setorNome: maq?.setorNome || null,
        competencia,
        competenciaMes,
        quinzena,
        createdAt: serverTimestamp(),
      });
    });

    await batch.commit();
    return maquinasElegiveis.length;
  };

  const carregarPendentes = async () => {
    if (!atendimentos.length) {
      setPendentes([]);
      setPendentesItensCount({});
      setEmergencialByAtendimento({});
      return;
    }
    setPendentesLoading(true);
    try {
      const pendentesTmp = [];
      const emergencialTmp = {};
      const contratoCache = new Map();

      const obterContratoAtivo = async (atendimentoRef, sessaoRef) => {
        const clienteRef = String(atendimentoRef?.clienteId || "").trim();
        if (!scope?.grupoId || !clienteRef) return null;

        const setorRef = String(
          sessaoRef?.setorId ||
          atendimentoRef?.setorId ||
          atendimentoRef?.academiaId ||
          ""
        ).trim();
        const cacheKey = `${clienteRef}|${setorRef}`;
        if (contratoCache.has(cacheKey)) return contratoCache.get(cacheKey);

        const validarContrato = (contrato) => {
          if (!contrato) return null;
          const status = String(contrato?.status || "").trim().toUpperCase();
          if (status !== "APROVADO") return null;
          const academiasIds = Array.isArray(contrato?.academiasIds)
            ? contrato.academiasIds.map((id) => String(id || "").trim()).filter(Boolean)
            : [];
          if (academiasIds.length > 0 && setorRef && !academiasIds.includes(setorRef)) return null;
          if (academiasIds.length > 0 && !setorRef) return null;
          return contrato;
        };

        const contratoIdRef = String(
          sessaoRef?.contratoId ||
          atendimentoRef?.contratoId ||
          ""
        ).trim();

        if (contratoIdRef) {
          try {
            const snap = await getDoc(doc(db, "financeiro_contratos", contratoIdRef));
            if (snap.exists()) {
              const contrato = validarContrato({ id: snap.id, ...snap.data() });
              if (contrato) {
                contratoCache.set(cacheKey, contrato);
                return contrato;
              }
            }
          } catch {
            // ignora fallback para query
          }
        }

        try {
          const filtros = [
            where("grupoId", "==", scope?.grupoId),
            where("clienteId", "==", clienteRef),
          ];
          if (setorRef) {
            filtros.push(where("academiasIds", "array-contains", setorRef));
          }
          const snapContratos = await getDocs(query(collection(db, "financeiro_contratos"), ...filtros));
          const lista = snapContratos.docs.map((d) => ({ id: d.id, ...d.data() }));
          const contratoAtivo = lista.map(validarContrato).find(Boolean) || null;
          contratoCache.set(cacheKey, contratoAtivo);
          return contratoAtivo;
        } catch {
          contratoCache.set(cacheKey, null);
          return null;
        }
      };

      await Promise.all(
        atendimentos.map(async (a) => {
          try {
            const empresaIdValue = a.empresaId || scope?.empresaId || a.clienteId || null;
            if (!empresaIdValue) return;
            const sess = await listarSessoesDoAtendimento(a.id, { empresaId: empresaIdValue });
            let temSessaoOsNaoLiberada = false;
            for (const s of sess) {
              const semTecnico = !s.tecnicoId;
              const semInicio = !s.inicioEm;
              const statusPend = String(s.status || "").toUpperCase() === "PENDENTE";
              const propostaAprovada =
                String(s?.propostaStatus || "").toUpperCase() === "APROVADA" || s?.liberadaAgendamento === true;

              if (!propostaAprovada) {
                let temOS = false;
                try {
                  const itensSessao = await listarItensSessao(s.id, { empresaId: empresaIdValue });
                  temOS = (Array.isArray(itensSessao) ? itensSessao : []).some((item) => {
                    const tipo = String(item?.tipo || "").toUpperCase();
                    return tipo === "OS" || !!item?.osId;
                  });
                } catch {
                  temOS = false;
                }
                if (temOS) temSessaoOsNaoLiberada = true;
                if (!temOS) continue;
              }

              if (semTecnico || semInicio || statusPend) {
                pendentesTmp.push({ ...s, atendimentoId: a.id });
              }
            }

            if (temSessaoOsNaoLiberada) {
              const contratoAtivo = await obterContratoAtivo(a, sess[0] || null);
              if (contratoAtivo) {
                const visitasEmergenciais = Number(contratoAtivo?.visitasEmergenciais ?? 0);
                const visitasUsadas = Number(contratoAtivo?.visitasEmergenciaisUsadas ?? 0);
                const visitasRestantes = Math.max(visitasEmergenciais - visitasUsadas, 0);
                if (visitasRestantes > 0) {
                  emergencialTmp[a.id] = {
                    disponivel: true,
                    restantes: visitasRestantes,
                  };
                }
              }
            }
          } catch {
            // ignora
          }
        })
      );

      pendentesTmp.sort((a, b) => {
        const da = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const dbv = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return dbv - da;
      });

      setPendentes(pendentesTmp);

      const counts = {};
      await Promise.all(
        pendentesTmp.map(async (s) => {
          try {
            const empresaIdValue = s.empresaId || scope?.empresaId || null;
            if (!empresaIdValue) {
              counts[s.id] = 0;
              return;
            }
            counts[s.id] = await contarItensSessao(s.id, { empresaId: empresaIdValue });
          } catch {
            counts[s.id] = 0;
          }
        })
      );
      setPendentesItensCount(counts);
      setEmergencialByAtendimento(emergencialTmp);
    } finally {
      setPendentesLoading(false);
    }
  };

  useEffect(() => {
    carregarSessoesSemana();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tecnicoId, isGlobalTecnico, weekStart, weekEnd, scope?.grupoId, scope?.empresaId]);

  useEffect(() => {
    carregarPendentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atendimentos]);

  useEffect(() => {
    if (!atendimentoSelecionado?.id) return;
    const aindaVisivel = leftPanelAtendimentos.some((item) => String(item.id) === String(atendimentoSelecionado.id));
    if (!aindaVisivel) {
      setAtendimentoSelecionado(null);
    }
  }, [leftPanelAtendimentos, atendimentoSelecionado?.id]);

  useEffect(() => {
    const carregarCounts = async () => {
      const map = new Map();
      [...sessoesSemana, ...pendentes].forEach((s) => {
        if (s?.id && !map.has(s.id)) map.set(s.id, s);
      });
      const sessoesParaCount = Array.from(map.values());

      if (!sessoesParaCount.length) {
        setSessaoItensCount({});
        setSessaoItensTipoCount({});
        setInstalacaoAtendimento({});
        return;
      }

      const counts = {};
      const countsByTipo = {};
      const periodicidadeByAtendimento = {};
      const instalacaoByAtendimento = {};
      await Promise.all(
        sessoesParaCount.map(async (s) => {
          try {
            const atendimento = atendimentosById[String(s.atendimentoId)] || {};
            const empresaIdValue =
              s.empresaId || atendimento.empresaId || atendimento.clienteId || scope?.empresaId || null;
            if (!empresaIdValue) {
              counts[s.id] = 0;
              countsByTipo[s.id] = { os: 0, preventiva: 0, equipamento: 0, total: 0 };
              return;
            }
            const itens = await listarItensSessao(s.id, { empresaId: empresaIdValue });
            let os = 0;
            let preventiva = 0;
            let equipamento = 0;
            let instalacao = 0;
            const periodicidadesSessao = [];
            (itens || []).forEach((item) => {
              const tipo = String(item?.tipo || "").toUpperCase();
              if (tipo === "OS" || item?.osId) {
                os += 1;
              } else if (tipo === "PREVENTIVA" || item?.preventivaId) {
                preventiva += 1;
                const per = normalizarPeriodicidade(item?.camadaPrevista || item?.periodicidade);
                if (per) periodicidadesSessao.push(per);
              } else if (tipo === "INSTALACAO" || item?.propostaInstalacaoId || item?.checklistInstalacaoId) {
                instalacao += 1;
                equipamento += 1;
              } else {
                equipamento += 1;
              }
            });

            const atendimentoId = String(s?.atendimentoId || "").trim();
            if (atendimentoId && instalacao > 0) {
              instalacaoByAtendimento[atendimentoId] = true;
            }
            if (atendimentoId && periodicidadesSessao.length) {
              const existentes = Array.isArray(periodicidadeByAtendimento[atendimentoId])
                ? periodicidadeByAtendimento[atendimentoId]
                : [];
              periodicidadeByAtendimento[atendimentoId] = [...existentes, ...periodicidadesSessao];
            }

            counts[s.id] = os + preventiva + equipamento;
            countsByTipo[s.id] = { os, preventiva, instalacao, equipamento, total: counts[s.id] };
          } catch {
            counts[s.id] = 0;
            countsByTipo[s.id] = { os: 0, preventiva: 0, instalacao: 0, equipamento: 0, total: 0 };
          }
        })
      );
      setSessaoItensCount(counts);
      setSessaoItensTipoCount(countsByTipo);
      setInstalacaoAtendimento(instalacaoByAtendimento);

      const periodicidadeResumo = {};
      Object.entries(periodicidadeByAtendimento).forEach(([atendimentoId, lista]) => {
        const maior = escolherMaiorPeriodicidade(lista);
        if (maior) periodicidadeResumo[atendimentoId] = maior;
      });
      setPeriodicidadeAtendimento(periodicidadeResumo);
    };

    carregarCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessoesSemana, pendentes, atendimentosById, scope?.empresaId]);

  const getTipoAtendimentoLabel = (atendimento) => {
    const atendimentoId = String(atendimento?.id || "").trim();
    if (atendimentoId && instalacaoAtendimento[atendimentoId]) return "Instalação";
    const periodicidade = atendimentoId ? periodicidadeAtendimento[atendimentoId] : "";
    return normalizeContratoTipo(periodicidade || atendimento?.contratoTipo);
  };

  const buscarAtendimentosParaSessoes = async () => {
    const ids = Array.from(new Set(sessoesSemana.map((s) => String(s.atendimentoId || "")))).filter(Boolean);
    const missing = ids.filter((id) => !atendimentosById[id]);
    if (!missing.length) return;
    const fetched = await Promise.all(
      missing.map(async (id) => {
        try {
          const ref = doc(db, "atendimentos_preventiva", id);
          const snap = await getDoc(ref);
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch (e) {
          console.warn("Falha ao carregar atendimento:", e);
        }
        return null;
      })
    );
    const adicionais = fetched.filter(Boolean);
    if (adicionais.length) {
      setAtendimentos((prev) => {
        const map = new Map(prev.map((a) => [a.id, a]));
        adicionais.forEach((a) => map.set(a.id, a));
        return Array.from(map.values());
      });
    }
  };

  useEffect(() => {
    if (sessoesSemana.length) {
      buscarAtendimentosParaSessoes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessoesSemana]);

  const resolveSetorId = (atendimentoRef, sessaoRef) => {
    return (
      atendimentoRef?.setorId ||
      atendimentoRef?.academiaId ||
      atendimentoRef?.setorID ||
      atendimentoRef?.academiaID ||
      atendimentoRef?.setor?.id ||
      atendimentoRef?.academia?.id ||
      atendimentoRef?.progresso?.setorId ||
      atendimentoRef?.progresso?.academiaId ||
      sessaoRef?.setorId ||
      null
    );
  };

  const carregarEnderecoAcademia = async ({ atendimentoRef, sessaoRef, enderecoFallback, setorIdHint }) => {
    let enderecoFinal = enderecoFallback || "";
    let setorId = setorIdHint || resolveSetorId(atendimentoRef, sessaoRef);

    if (!setorId && sessaoRef?.id) {
      try {
        const empresaIdValue =
          sessaoRef.empresaId || atendimentoRef?.empresaId || scope?.empresaId || atendimentoRef?.clienteId || null;
        if (empresaIdValue) {
          const itens = await listarItensSessaoResumo(sessaoRef.id, 5, { empresaId: empresaIdValue });
          const itemComSetor = itens.find((i) => i?.setorId);
          if (itemComSetor?.setorId) setorId = itemComSetor.setorId;
        }
      } catch {
        // ignora, usa fallback
      }
    }

    if (setorId) {
      try {
        const ref = doc(db, "setores", setorId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.endereco) enderecoFinal = data.endereco;
        }
      } catch {
        // ignora, usa fallback
      }
    }

    if (enderecoFinal !== undefined) {
      setModalEnderecoDestino(enderecoFinal || "");
    }
  };

  const abrirModalSessao = ({ atendimento, baseDate, sessao }) => {
    const atendimentoRef = atendimento || (sessao ? atendimentosById[sessao.atendimentoId] : null);
    const clienteRef = atendimentoRef ? clientesById[atendimentoRef.clienteId] : null;
    const enderecoCliente = clienteRef?.endereco || "";
    const setorIdBase = resolveSetorId(atendimentoRef, sessao);

    if (sessao) {
      const semTecnico = !sessao.tecnicoId;
      const semInicio = !sessao.inicioEm;
      const statusPend = String(sessao.status || "").toUpperCase() === "PENDENTE";
      setModalMode(semTecnico || semInicio || statusPend ? "assign" : "edit");
      setModalSessaoId(sessao.id);
      setModalAtendimento(atendimentoRef);
      const contratoTipoAtendimento = String(atendimentoRef?.contratoTipo || "").trim().toUpperCase();
      const atendimentoAvulso = !contratoTipoAtendimento || contratoTipoAtendimento === "AVULSO";
      const emergencialDetectado =
        sessao?.emergencialContrato === true
        || (
          atendimentoAvulso
          && !!sessao?.contratoId
          && String(sessao?.contratoStatus || "").trim().toUpperCase() === "APROVADO"
          && String(sessao?.propostaStatus || "").trim().toUpperCase() === "APROVADA"
        );
      setModalEmergencialContrato(emergencialDetectado);
      const tecnicoInicial = String(sessao.tecnicoId || tecnicoId || "").trim();
      setModalTecnicoId(tecnicoInicial);
      const acompanhantesIniciais = Array.isArray(sessao.tecnicosAcompanhantesIds)
        ? sessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim()).filter(Boolean)
        : [];
      setModalTecnicosAcompanhantesIds(acompanhantesIniciais.filter((id) => id !== tecnicoInicial));
      setModalAgendaBloqueada(sessao.agendaBloqueada === true);
      setModalMotivoBloqueio(sessao.motivoBloqueio || "");
      setModalMotivoRemanejamento("");
      if (sessao.inicioEm) {
        const inicioBase = toDateTimeInput(sessao.inicioEm);
        const fimBase = toDateTimeInput(sessao.fimEm);
        setModalInicio(inicioBase);
        setModalFim(fimBase);
        setModalOriginalAgendamento({
          inicio: inicioBase,
          fim: fimBase,
          tecnicoId: tecnicoInicial,
        });
      } else {
        const base = baseDate ? new Date(baseDate) : new Date();
        base.setHours(8, 0, 0, 0);
        const fim = new Date(base);
        fim.setHours(12, 0, 0, 0);
        const inicioBase = toDateTimeInput(base);
        const fimBase = toDateTimeInput(fim);
        setModalInicio(inicioBase);
        setModalFim(fimBase);
        setModalOriginalAgendamento({
          inicio: inicioBase,
          fim: fimBase,
          tecnicoId: tecnicoInicial,
        });
      }
      const st = String(sessao.status || "").toUpperCase();
      setModalStatus(!st || st === "PENDENTE" || st === "PLANEJADA" ? "AGENDADA" : sessao.status);
      setModalObs(sessao.observacoes || "");
      setModalKmRodados(sessao.kmRodados ?? "");
      // Carregar kmIda e kmVolta se existirem (sessões novas que calcularam distância)
      setModalKmIda(sessao.kmIda ?? 0);
      setModalKmVolta(sessao.kmVolta ?? 0);
      setModalValorDeslocamento(sessao.valorDeslocamento ?? "");
      setModalValorPedagio(sessao.valorPedagio ?? "");
      setModalPedagioIda(sessao.pedagioIda ?? 0);
      setModalPedagioVolta(sessao.pedagioVolta ?? 0);
      setModalValorMaoObra(sessao.valorMaoObra ?? "");
      setModalValorPecas(sessao.valorPecas ?? "");
      setModalValorDesconto(sessao.valorDesconto ?? "");
      setModalValorTotal(sessao.valorTotal ?? "");
      setModalIdaVolta(sessao.idaVolta ?? true);
      const enderecoBase = setorIdBase ? (sessao.enderecoDestino || "") : (sessao.enderecoDestino || enderecoCliente || "");
      setModalEnderecoDestino(enderecoBase);
      setModalDestinoCoords(sessao.destinoLat && sessao.destinoLng ? { lat: sessao.destinoLat, lng: sessao.destinoLng } : null);
			// Considera calculado se já existe km de ida/rodados salvo na sessão
			setModalDistanciaCalculada(
				Number(sessao.kmIda ?? 0) > 0 || Number(sessao.kmRodados ?? 0) > 0
			);
      carregarEnderecoAcademia({
        atendimentoRef,
        sessaoRef: sessao,
        enderecoFallback: enderecoBase,
        setorIdHint: setorIdBase,
      });
    } else {
      // Não cria sessão aqui para evitar duplicidade e sessões vazias.
      setErro("Não existe visita planejada para este atendimento. Crie a visita (com itens) primeiro.");
      if (atendimentoRef) abrirNovaVisitaDoAtendimento(atendimentoRef);
      return;
    }
    setModalOpen(true);
  };

  const abrirResumoSessao = async (sessao) => {
    if (!sessao?.id) return;
    setResumoSessao(sessao);
    setResumoOpen(true);
    setResumoLoading(true);
    try {
      const atendimento = atendimentosById[sessao.atendimentoId] || {};
      const empresaIdResumo = sessao.empresaId || atendimento.empresaId || atendimento.clienteId || scope?.empresaId || null;
      if (!empresaIdResumo) throw new Error("empresaId nao informado");
      const total = await contarItensSessao(sessao.id, { empresaId: empresaIdResumo });
      const itens = await listarItensSessaoResumo(sessao.id, 10, { empresaId: empresaIdResumo });
      setResumoTotal(total);
      setResumoItens(itens);
    } catch (e) {
      console.error("Erro ao carregar resumo da sessao:", e);
      setResumoTotal(0);
      setResumoItens([]);
    } finally {
      setResumoLoading(false);
    }
  };

  const handleAgendarDia = (day) => {
    if (!atendimentoSelecionado) {
      setErro("Selecione um atendimento para agendar.");
      return;
    }
    if (isGlobalTecnico) {
      setErro("Selecione um tecnico para agendar.");
      return;
    }

    const pendentesDoAtendimento = pendentes.filter(
      (s) => String(s.atendimentoId || "") === String(atendimentoSelecionado.id)
    );
    const base = new Date(day);
    base.setHours(8, 0, 0, 0);

    // Ordenar visitas pendentes por quantidade de itens (maior primeiro), depois por data de criação
    const ordenadas = [...pendentesDoAtendimento].sort((a, b) => {
      const ca = Number(pendentesItensCount[a.id] || 0);
      const cb = Number(pendentesItensCount[b.id] || 0);
      if (cb !== ca) return cb - ca;
      const da = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const dbv = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return dbv - da;
    });

    // Usar a primeira visita ordenada (mais recente ou com mais itens)
    const primeira = ordenadas[0] || null;
    if (primeira && Number(pendentesItensCount[primeira.id] || 0) === 0) {
      setErro("Essa visita está sem itens. Crie/complete a visita (com itens) antes de agendar.");
      abrirNovaVisitaDoAtendimento(atendimentoSelecionado);
      return;
    }

    const abrirModal = (sessaoRef) => {
      abrirModalSessao({
        atendimento: atendimentoSelecionado,
        baseDate: base,
        sessao: sessaoRef,
      });
    };

    const tentarLiberarComCotaEmergencial = async () => {
      if (!primeira?.id) return false;

      const empresaIdSessao =
        primeira?.empresaId ||
        atendimentoSelecionado?.empresaId ||
        atendimentoSelecionado?.clienteId ||
        scope?.empresaId ||
        null;
      if (!empresaIdSessao) return false;

      let itens = [];
      try {
        itens = await listarItensSessao(primeira.id, { empresaId: empresaIdSessao });
      } catch {
        return false;
      }

      const itensOS = (Array.isArray(itens) ? itens : []).filter((item) => {
        const tipo = String(item?.tipo || "").toUpperCase();
        return tipo === "OS" || !!item?.osId;
      });
      if (!itensOS.length) return false;

      const osIds = Array.from(
        new Set(
          itensOS.map((item) => String(item?.osId || "").trim()).filter(Boolean)
        )
      );
      if (!osIds.length) return false;

      const osDocs = await Promise.all(osIds.map(async (osId) => {
        try {
          const snap = await getDoc(doc(db, "ordensServico", osId));
          return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        } catch {
          return null;
        }
      }));
      const osValidas = osDocs.filter(Boolean);
      if (!osValidas.length) return false;

      const temSomenteCorretiva = osValidas.every((os) => isCorretiva(os?.tipoServico));
      if (!temSomenteCorretiva) return false;

      const contratoIdSessao =
        primeira?.contratoId ||
        atendimentoSelecionado?.contratoId ||
        null;

      let contrato = null;
      if (contratoIdSessao) {
        try {
          const contratoSnap = await getDoc(doc(db, "financeiro_contratos", contratoIdSessao));
          if (contratoSnap.exists()) {
            contrato = { id: contratoSnap.id, ...contratoSnap.data() };
          }
        } catch {
          contrato = null;
        }
      }

      if (!contrato) {
        if (!scope?.grupoId || !atendimentoSelecionado?.clienteId) return false;
        const filtros = [
          where("grupoId", "==", scope?.grupoId),
          where("clienteId", "==", atendimentoSelecionado?.clienteId),
        ];
        const setorIdAtendimento = atendimentoSelecionado?.setorId || atendimentoSelecionado?.academiaId || null;
        if (setorIdAtendimento) {
          filtros.push(where("academiasIds", "array-contains", setorIdAtendimento));
        }
        try {
          const snapContratos = await getDocs(query(collection(db, "financeiro_contratos"), ...filtros));
          const lista = snapContratos.docs.map((d) => ({ id: d.id, ...d.data() }));
          contrato = lista.find((c) => isContratoAtivo(c?.status)) || null;
        } catch {
          contrato = null;
        }
      }

      if (!contrato || !isContratoAtivo(contrato.status)) return false;

      const visitasEmergenciais = Number(contrato?.visitasEmergenciais ?? 0);
      const visitasUsadas = Number(contrato?.visitasEmergenciaisUsadas ?? 0);
      const visitasRestantes = Math.max(visitasEmergenciais - visitasUsadas, 0);
      if (visitasRestantes <= 0) return false;

      await updateDoc(doc(db, "atendimentos_sessoes", primeira.id), {
        propostaStatus: "APROVADA",
        liberadaAgendamento: true,
        contratoId: contrato.id,
        contratoStatus: "APROVADO",
        emergencialContrato: true,
        emergencialOrigem: "CONTRATO_COTA",
        emergencialConsumoPendente: true,
        emergencialConsumido: false,
        updatedAt: serverTimestamp(),
      });

      const sessaoAtualizada = {
        ...primeira,
        propostaStatus: "APROVADA",
        liberadaAgendamento: true,
        contratoId: contrato.id,
        contratoStatus: "APROVADO",
        emergencialContrato: true,
        emergencialOrigem: "CONTRATO_COTA",
        emergencialConsumoPendente: true,
        emergencialConsumido: false,
      };
      abrirModal(sessaoAtualizada);
      setSucesso("Visita emergencial de contrato acionada e liberada para agendamento.");
      return true;
    };

    const agendar = async () => {
      const liberada =
        String(primeira?.propostaStatus || "").toUpperCase() === "APROVADA" ||
        primeira?.liberadaAgendamento === true;

      if (liberada) {
        abrirModal(primeira);
        return;
      }

      const liberouPorEmergencial = await tentarLiberarComCotaEmergencial();
      if (liberouPorEmergencial) {
        await carregarPendentes();
        return;
      }

      setErro(
        "Não é possível agendar técnico para esta visita.\n\n" +
        "A visita ainda não está liberada para agendamento (proposta/contrato pendente)."
      );
    };

    void agendar();
  };

  const handleDeletarAtendimento = async (atendimentoId) => {
    if (!atendimentoId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este atendimento? As sessões vinculadas também serão removidas. Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    try {
      await excluirAtendimento(atendimentoId);
      await carregarAtendimentos();
      await carregarPendentes();
      setAtendimentoSelecionado(null);
    } catch (e) {
      console.error("Erro ao excluir atendimento:", e);
      setErro(`Erro ao excluir atendimento: ${e.message || "Tente novamente"}`);
    }
  };

  const handleAcompanharServico = async (sessao, { quantidadeServicos = 1 } = {}) => {
    const sessaoId = String(sessao?.id || "").trim();
    if (!sessaoId) {
      setErro("Sessão inválida para acompanhamento.");
      return;
    }
    if (!tecnicoId || isGlobalTecnico) {
      setErro("Selecione um técnico específico para acompanhar o serviço.");
      return;
    }

    const tecnicoPrincipalId = String(sessao?.tecnicoId || "").trim();
    const tecnicoSelecionadoId = String(tecnicoId || "").trim();
    if (tecnicoPrincipalId && tecnicoPrincipalId === tecnicoSelecionadoId) {
      setErro("Este técnico já é o principal da visita.");
      return;
    }

    const acompanhantesIdsAtuais = Array.isArray(sessao?.tecnicosAcompanhantesIds)
      ? sessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];

    if (acompanhantesIdsAtuais.includes(tecnicoSelecionadoId)) {
      setSucesso("Técnico já está como acompanhante deste serviço.");
      return;
    }

    const acompanhantesIdsNovos = Array.from(
      new Set(
        [...acompanhantesIdsAtuais, tecnicoSelecionadoId].filter((id) => id && id !== tecnicoPrincipalId)
      )
    );

    const acompanhantesNomesNovos = acompanhantesIdsNovos
      .map((id) => {
        const tecnico = tecnicosById[id];
        return tecnico?.nome || tecnico?.email || "Técnico";
      })
      .filter(Boolean);

    const tecnicoSelecionadoNome = tecnicosById[tecnicoSelecionadoId]?.nome || tecnicosById[tecnicoSelecionadoId]?.email || "Técnico";
    const tecnicoPrincipalNome = tecnicosById[tecnicoPrincipalId]?.nome || tecnicosById[tecnicoPrincipalId]?.email || "Técnico";
    const quantidade = Math.max(Number(quantidadeServicos) || 1, 1);
    const confirmar = window.confirm(
      `Confirmar acompanhamento?\n\nO técnico ${tecnicoSelecionadoNome} irá acompanhar o técnico ${tecnicoPrincipalNome} em ${quantidade} serviço(s).`
    );
    if (!confirmar) return;

    setErro("");
    setSucesso("");
    try {
      await updateDoc(doc(db, "atendimentos_sessoes", sessaoId), {
        tecnicosAcompanhantesIds: acompanhantesIdsNovos,
        tecnicosAcompanhantesNomes: acompanhantesNomesNovos,
        updatedAt: serverTimestamp(),
      });
      setSucesso("Serviço marcado para acompanhamento na mesma data e horário.");
      await carregarSessoesSemana();
      await carregarPendentes();
    } catch (e) {
      console.error("Erro ao acompanhar serviço:", e);
      setErro("Não foi possível acompanhar este serviço.");
    }
  };

  const handleCalcularDistancia = async () => {
    if (!enderecoOrigem) {
      setErro("Endereço de origem não configurado. Configure em Opções > Grupo.");
      return;
    }

    if (!modalEnderecoDestino) {
      setErro("Informe o endereço de destino.");
      return;
    }

    setModalCalculandoDistancia(true);
    setErro("");

    try {
      // Calcular distância da ida
      const resultadoIda = await calcularDistancia(grupoId, enderecoOrigem, modalEnderecoDestino);
      const kmIda = resultadoIda.distanceKm;
      let totalKm = kmIda;
      let mensagem = `Distância (ida): ${kmIda} km (${resultadoIda.durationText})`;

      // Se ida e volta, somar a volta também
      let kmVolta = 0;
      if (modalIdaVolta) {
        const resultadoVolta = await calcularDistancia(grupoId, modalEnderecoDestino, enderecoOrigem);
        kmVolta = resultadoVolta.distanceKm;
        totalKm += kmVolta;
        mensagem = `Ida: ${kmIda} km | Volta: ${kmVolta} km | Total: ${totalKm} km`;
      }

      // Armazenar km de ida e volta separadamente para poder recalcular quando modalIdaVolta muda
      setModalKmIda(kmIda);
      setModalKmVolta(kmVolta);
      setModalKmRodados(totalKm.toString());
			setModalDistanciaCalculada(true);
      setSucesso(mensagem);

      try {
        // Pedágio da ida
        const tollsIda = await apiMapsTolls({
          grupoId,
          origin: enderecoOrigem,
          destination: modalEnderecoDestino,
        });
        const tollValueIda = Number(tollsIda?.tollValue || 0);
        setModalPedagioIda(tollValueIda);

        // Se ida e volta, calcular pedágio da volta também
        if (modalIdaVolta) {
          const tollsVolta = await apiMapsTolls({
            grupoId,
            origin: modalEnderecoDestino,
            destination: enderecoOrigem,
          });
          const tollValueVolta = Number(tollsVolta?.tollValue || 0);
          setModalPedagioVolta(tollValueVolta);
        } else {
          setModalPedagioVolta(0);
        }
      } catch (tollErr) {
        console.warn("Falha ao calcular pedágio:", tollErr);
      }
    } catch (err) {
      console.error("Erro ao calcular distância:", err);
      setErro(err.message || "Erro ao calcular distância");
    } finally {
      setModalCalculandoDistancia(false);
    }
  };


  const handleSalvarSessao = async () => {
    if (!modalAtendimento?.id) return;
    if (!modalInicio) {
      setErro("Informe inicio da sessao.");
      return;
    }
    const inicioSessao = new Date(modalInicio);
    if (Number.isNaN(inicioSessao.getTime())) {
      setErro("Data/hora de início inválida.");
      return;
    }
    const agora = new Date();
    const tentandoAgendar = modalMode === "assign";
    const tentandoRemanejarParaPassado = modalMode === "edit" && modalTemRemanejamento;
    if ((tentandoAgendar || tentandoRemanejarParaPassado) && inicioSessao.getTime() < agora.getTime()) {
      setErro("Não é permitido agendar visita em data/hora passada.");
      return;
    }
    if (!modalTecnicoId) {
      setErro("Selecione o técnico principal da sessão.");
      return;
    }
    if (modalMode === "edit" && modalAgendaBloqueada && modalTemRemanejamento && !canGerenciarBloqueio) {
      setErro("Agenda bloqueada para esta sessão. Somente admin/gestor pode remanejar data ou técnico.");
      return;
    }
    if (modalMode === "edit" && modalTemRemanejamento && !String(modalMotivoRemanejamento || "").trim()) {
      setErro("Informe o motivo do remanejamento para alterar data ou técnico.");
      return;
    }
    if (modalAgendaBloqueada && !String(modalMotivoBloqueio || "").trim()) {
      setErro("Informe o motivo do congelamento da agenda.");
      return;
    }
    if (modalTecnicosAcompanhantesIds.includes(modalTecnicoId)) {
      setErro("O técnico principal não pode estar na lista de acompanhantes.");
      return;
    }
    if (!modalIsContrato && modalMode === "assign" && !modalDistanciaCalculada) {
      setErro("Para atendimentos avulsos, é obrigatório calcular a distância antes de salvar.");
      return;
    }
    const empresaIdSessao =
      modalAtendimento?.empresaId ||
      modalAtendimento?.clienteId ||
      scope?.empresaId ||
      (clienteId && clienteId !== "__GLOBAL__" ? clienteId : null);
    if (!empresaIdSessao) {
      setErro("Selecione uma empresa para salvar a sessao.");
      return;
    }
    setErro("");
    setSucesso("");
    setSalvandoSessao(true);
    try {
      const financeiroPayload = modalIsContrato
        ? {
            kmRodados: modalKmRodados,
            kmIda: modalKmIda,
            kmVolta: modalKmVolta,
            enderecoDestino: modalEnderecoDestino,
            destinoLat: modalDestinoCoords?.lat || null,
            destinoLng: modalDestinoCoords?.lng || null,
            idaVolta: modalIdaVolta,
            valorDeslocamento: "0",
            valorPedagio: "0",
            pedagioIda: "0",
            pedagioVolta: "0",
            valorMaoObra: "0",
            valorPecas: "0",
            valorDesconto: "0",
            valorTotal: "0",
          }
        : {
            kmRodados: modalKmRodados,
            kmIda: modalKmIda,
            kmVolta: modalKmVolta,
            enderecoDestino: modalEnderecoDestino,
            destinoLat: modalDestinoCoords?.lat || null,
            destinoLng: modalDestinoCoords?.lng || null,
            idaVolta: modalIdaVolta,
            valorDeslocamento: modalValorDeslocamento,
            valorPedagio: modalValorPedagio,
            pedagioIda: modalPedagioIda,
            pedagioVolta: modalPedagioVolta,
            valorMaoObra: modalValorMaoObra,
            valorPecas: modalValorPecas,
            valorDesconto: modalValorDesconto,
            valorTotal: modalValorTotal,
          };
      if (modalMode === "edit") {
        await atualizarSessao(
          modalSessaoId,
          new Date(modalInicio),
          modalFim ? new Date(modalFim) : null,
          modalStatus,
          modalObs,
          modalTecnicoId, financeiroPayload
        );

        const acompanhantesInfo = modalTecnicosAcompanhantesIds
          .map((id) => {
            const t = tecnicosById[id];
            return { id, nome: t?.nome || t?.email || "Técnico" };
          })
          .filter((item) => !!item.id);

        const payloadExtra = {
          tecnicosAcompanhantesIds: acompanhantesInfo.map((item) => item.id),
          tecnicosAcompanhantesNomes: acompanhantesInfo.map((item) => item.nome),
          agendaBloqueada: modalAgendaBloqueada,
          motivoBloqueio: modalAgendaBloqueada ? String(modalMotivoBloqueio || "").trim() : null,
          bloqueadaPorId: modalAgendaBloqueada ? (usuario?.uid || null) : null,
          bloqueadaPorNome: modalAgendaBloqueada ? (usuario?.nome || usuario?.displayName || usuario?.email || null) : null,
          bloqueadaEm: modalAgendaBloqueada ? serverTimestamp() : null,
          updatedAt: serverTimestamp(),
        };

        if (modalTemRemanejamento) {
          payloadExtra.ultimaMudancaAgenda = {
            deInicio: modalOriginalAgendamento?.inicio || null,
            deFim: modalOriginalAgendamento?.fim || null,
            deTecnicoId: modalOriginalAgendamento?.tecnicoId || null,
            paraInicio: modalInicio || null,
            paraFim: modalFim || null,
            paraTecnicoId: modalTecnicoId || null,
            motivo: String(modalMotivoRemanejamento || "").trim(),
            alteradoEm: new Date().toISOString(),
            alteradoPorId: usuario?.uid || null,
            alteradoPorNome: usuario?.nome || usuario?.displayName || usuario?.email || null,
          };
          payloadExtra.historicoRemanejamentos = arrayUnion(payloadExtra.ultimaMudancaAgenda);
        }

        await updateDoc(doc(db, "atendimentos_sessoes", modalSessaoId), payloadExtra);
        await garantirItensInstalacaoDaSessao({ sessaoId: modalSessaoId, atendimento: modalAtendimento });
        setSucesso("Sessao atualizada.");
      } else {
        if (!modalTecnicoId || modalTecnicoId === "__GLOBAL__") {
          setErro("Selecione um técnico para agendar.");
          setSalvandoSessao(false);
          return;
        }
        if (!modalSessaoId) {
          setErro("Sessão não informada. Crie a visita (com itens) primeiro.");
          setSalvandoSessao(false);
          if (modalAtendimento) abrirNovaVisitaDoAtendimento(modalAtendimento);
          return;
        }
        await atualizarSessao(
          modalSessaoId,
          new Date(modalInicio),
          modalFim ? new Date(modalFim) : null,
          modalStatus,
          modalObs,
          modalTecnicoId, financeiroPayload
        );

        const acompanhantesInfo = modalTecnicosAcompanhantesIds
          .map((id) => {
            const t = tecnicosById[id];
            return { id, nome: t?.nome || t?.email || "Técnico" };
          })
          .filter((item) => !!item.id);

        await updateDoc(doc(db, "atendimentos_sessoes", modalSessaoId), {
          tecnicosAcompanhantesIds: acompanhantesInfo.map((item) => item.id),
          tecnicosAcompanhantesNomes: acompanhantesInfo.map((item) => item.nome),
          agendaBloqueada: modalAgendaBloqueada,
          motivoBloqueio: modalAgendaBloqueada ? String(modalMotivoBloqueio || "").trim() : null,
          bloqueadaPorId: modalAgendaBloqueada ? (usuario?.uid || null) : null,
          bloqueadaPorNome: modalAgendaBloqueada ? (usuario?.nome || usuario?.displayName || usuario?.email || null) : null,
          bloqueadaEm: modalAgendaBloqueada ? serverTimestamp() : null,
          updatedAt: serverTimestamp(),
        });
        await garantirItensInstalacaoDaSessao({ sessaoId: modalSessaoId, atendimento: modalAtendimento });
        setSucesso("Sessao agendada.");
      }
      setModalOpen(false);
      await carregarSessoesSemana();
      await carregarContagemSessoes(atendimentos);
      await carregarPendentes();
    } catch (e) {
      console.error("Erro ao salvar sessao:", e);
      setErro("Erro ao salvar sessao.");
    } finally {
      setSalvandoSessao(false);
    }
  };

  const header = (
    <section className="pc-card pc-table-card">
      <header className="pc-table-header">
        <div className="pc-table-header-text">
          <h3>{titulo}</h3>
          <p>{descricao}</p>
        </div>
      </header>

      {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
      {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

      <div className="planejamento-visitas-filtros">
        <label>
          Técnico *
          <select value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
            <option value="">Selecione</option>
            {canGlobalView && <option value="__GLOBAL__">Global</option>}
            {tecnicos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome || t.email || "Técnico"}
              </option>
            ))}
          </select>
        </label>

        <label>
          Cliente
          <select
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            disabled={!modoGlobal}
            title={!modoGlobal ? "Escopo fixo de empresa" : ""}
          >
            <option value="">Selecione</option>
            {modoGlobal && <option value="__GLOBAL__">Global</option>}
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome || c.id}
              </option>
            ))}
          </select>
        </label>

        <label>
          Competência
          <input type="month" value={competenciaMes} onChange={(e) => setCompetenciaMes(e.target.value)} />
        </label>

        <label>
          Quinzena
          <select value={quinzena} onChange={(e) => setQuinzena(e.target.value)}>
            <option value="TODAS">Todas</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
          </select>
        </label>

        <label>
          Status
          <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
            {STATUS_ATENDIMENTO.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );

  const leftPanel = (
    <section className="pc-card agendamentos-panel">
      <div className="agendamentos-panel-body">
        <div className="itens-lista">
          {leftPanelAtendimentos.map((a) => {
            const cliente = clientesById[a.clienteId]?.nome || "-";
            const info = atendimentosInfo[a.id] || { academiaNome: "-", osCount: 0, preventivaCount: 0 };
            const emergencial = emergencialByAtendimento[a.id];
            const sessoesDoAtendimento = sessoesVisiveisPorAtendimento[String(a.id)] || [];
            const pendenciasDoAtendimento = pendenciasPorAtendimento[String(a.id)] || [];
            const pendentesDoAtendimento = pendenciasDoAtendimento.length;
            const sessoesAgendadasEmOutroTecnico = (isGlobalTecnico || !tecnicoId)
              ? []
              : sessoesDoAtendimento.filter((sessaoItem) => {
                const sessaoTecnicoId = String(sessaoItem?.tecnicoId || "").trim();
                if (!sessaoTecnicoId || sessaoTecnicoId === String(tecnicoId || "").trim()) return false;
                const statusSessao = String(sessaoItem?.status || "").toUpperCase();
                if (statusSessao === "FINALIZADA" || statusSessao === "CANCELADA") return false;
                const acompanhantesIds = Array.isArray(sessaoItem?.tecnicosAcompanhantesIds)
                  ? sessaoItem.tecnicosAcompanhantesIds.map((id) => String(id || "").trim())
                  : [];
                return !acompanhantesIds.includes(String(tecnicoId || "").trim());
              });
            const tecnicosAgendadosOutroNome = Array.from(new Set(
              sessoesAgendadasEmOutroTecnico
                .map((sessaoItem) => {
                  const sessaoTecnicoId = String(sessaoItem?.tecnicoId || "").trim();
                  return tecnicosById[sessaoTecnicoId]?.nome || sessaoTecnicoId;
                })
                .filter(Boolean)
            ));
            const sessoesOrdenadasOutroTecnico = [...sessoesAgendadasEmOutroTecnico].sort((a, b) => {
              const da = a?.inicioEm?.toDate ? a.inicioEm.toDate().getTime() : new Date(a?.inicioEm || a?.createdAt || 0).getTime();
              const dbv = b?.inicioEm?.toDate ? b.inicioEm.toDate().getTime() : new Date(b?.inicioEm || b?.createdAt || 0).getTime();
              return da - dbv;
            });
            const sessaoAcompanhamento = sessoesOrdenadasOutroTecnico.find((sessaoItem) => {
              const acompanhantesIds = Array.isArray(sessaoItem?.tecnicosAcompanhantesIds)
                ? sessaoItem.tecnicosAcompanhantesIds.map((id) => String(id || "").trim())
                : [];
              return !acompanhantesIds.includes(String(tecnicoId || "").trim());
            }) || sessoesOrdenadasOutroTecnico[0] || null;
            const jaAcompanhando = !!sessaoAcompanhamento && Array.isArray(sessaoAcompanhamento?.tecnicosAcompanhantesIds)
              && sessaoAcompanhamento.tecnicosAcompanhantesIds
                .map((id) => String(id || "").trim())
                .includes(String(tecnicoId || "").trim());
            const agendadoEmOutroTecnico = tecnicosAgendadosOutroNome.length > 0;
            return (
              <div
                key={a.id}
                className={`item-card ${atendimentoSelecionado?.id === a.id ? "selecionado" : ""} ${agendadoEmOutroTecnico ? "agendado-outro-tecnico" : ""}`}
                onClick={() => setAtendimentoSelecionado(a)}
                role="button"
                tabIndex={0}
              >
                <div className="item-titulo">{cliente} • {info.academiaNome} • {getTipoAtendimentoLabel(a)}</div>
                <div className="item-detalhe">{a.competencia || "-"} • {a.status || "-"}</div>
                {agendadoEmOutroTecnico && (
                  <div className="item-detalhe item-detalhe-aviso">
                    Agendado para: {tecnicosAgendadosOutroNome.join(", ")}
                  </div>
                )}
                {agendadoEmOutroTecnico && sessaoAcompanhamento?.inicioEm && (
                  <div className="item-detalhe item-detalhe-aviso">
                    Data/hora: {formatDateTime(sessaoAcompanhamento.inicioEm)}
                  </div>
                )}
                <div className="item-detalhe">
                  OS: {info.osCount} • Preventivas: {info.preventivaCount} • Sessões pendentes: {pendentesDoAtendimento}
                </div>
                {emergencial?.disponivel && (
                  <div className="item-detalhe" style={{ color: "#f59e0b", fontWeight: 700 }}>
                    ⚡ Emergencial disponível ({emergencial.restantes})
                  </div>
                )}
                <div className="item-detalhe">
                  Alvo: {a.progresso?.equipamentosAlvo ?? "-"}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {agendadoEmOutroTecnico && sessaoAcompanhamento && !isGlobalTecnico && tecnicoId && (
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      disabled={jaAcompanhando}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcompanharServico(sessaoAcompanhamento, { quantidadeServicos: sessoesAgendadasEmOutroTecnico.length });
                      }}
                      title={jaAcompanhando ? "Este técnico já acompanha esta visita" : "Acompanhar esta visita na mesma data e horário"}
                    >
                      {jaAcompanhando ? "Acompanhando" : "Acompanhar serviço"}
                    </button>
                  )}
                  {!agendadoEmOutroTecnico && (
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAtendimentoSelecionado(a);
                      }}
                    >
                      Selecionar
                    </button>
                  )}
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletarAtendimento(a.id);
                    }}
                    title="Deletar este atendimento"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );

  const rightPanel = (
    <section className="pc-card agendamentos-panel">
      <header className="agendamentos-panel-header">
        <div className="agendamentos-panel-header-row">
          <h3>Agenda semanal</h3>
          <input
            type="date"
            className="agendamentos-header-date"
            value={dataVizualização.toISOString().slice(0, 10)}
            onChange={(e) => {
              const newDate = new Date(e.target.value + "T00:00:00");
              if (!Number.isNaN(newDate.getTime())) {
                const nextDay = startOfDay(newDate);
                setDataVizualizacao(nextDay);
                setCompetenciaMes(monthString(nextDay));
              }
            }}
          />
        </div>
      </header>
      <div className="agendamentos-panel-body">
        <div className="agendamentos-week-controls">
          <button type="button" className="pc-btn-secundario" onClick={irParaMêsAnterior} title="Mês anterior">
            Mês ←
          </button>
          <button type="button" className="pc-btn-secundario" onClick={irParaSemanaAnterior} title="Semana anterior">
            Semana ←
          </button>
          <button type="button" className="pc-btn-secundario" onClick={irParaHoje} title="Ir para hoje">
            Hoje
          </button>
          <button type="button" className="pc-btn-secundario" onClick={irParaSemanaSeguinte} title="Semana seguinte">
            Semana →
          </button>
          <button type="button" className="pc-btn-secundario" onClick={irParaPróximoMês} title="Próximo mês">
            Mês →
          </button>
        </div>
        {!tecnicoId && <div className="pc-alert">Selecione um técnico.</div>}
        {tecnicoId && (
          <div className="agenda-list">
            {weekDays.map((d) => {
              const key = startOfDay(d).toISOString().slice(0, 10);
              const dayName = ["Domingo", "Segunda-feira", "Terca-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sabado"][d.getDay()];
              const list = sessoesPorDia[key] || [];
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;

              return (
                <div key={key} className={`agenda-day-card ${isWeekend ? "fim-semana" : ""}`}>
                  <div className="agenda-day-header">
                    <div>
                      <div className="agenda-day-title">{dayName}</div>
                      <div className="agenda-day-date">{fmtDate(d)}</div>
                    </div>
                    <div className="agenda-day-actions">
                      <button
                        type="button"
                        className="pc-btn-primario"
                        disabled={!atendimentoSelecionado || isGlobalTecnico}
                        onClick={() => handleAgendarDia(d)}
                      >
                        {isGlobalTecnico
                          ? "Selecione tecnico"
                          : atendimentoSelecionado
                            ? "Agendar"
                            : "Selecione atendimento"}
                      </button>
                    </div>
                  </div>
                  <div className="agenda-day-body">
                    {list.length === 0 && (
                      <div className="agenda-empty-day">Nenhum atendimento.</div>
                    )}
                    {list.map((s) => {
                      const atendimento = atendimentosById[s.atendimentoId] || {};
                      const cliente = clientesById[atendimento.clienteId]?.nome || "-";
                      const info = atendimentosInfo[s.atendimentoId] || { academiaNome: "-", osCount: 0, preventivaCount: 0 };
                      const counts = sessaoItensTipoCount[s.id] || { os: 0, preventiva: 0, instalacao: 0, equipamento: 0, total: 0 };
                      const equipamentoLabel = Number(counts.instalacao || 0) > 0 ? "Instalações" : "Equipamentos";
                      const tecnicoNome = tecnicosById[s.tecnicoId]?.nome || "-";
                      const acompanhantesNomes = Array.isArray(s.tecnicosAcompanhantesNomes)
                        ? s.tecnicosAcompanhantesNomes.filter(Boolean)
                        : [];
                      const ini = s.inicioEm ? (s.inicioEm.toDate ? s.inicioEm.toDate() : new Date(s.inicioEm)) : null;
                      const fim = s.fimEm ? (s.fimEm.toDate ? s.fimEm.toDate() : new Date(s.fimEm)) : null;
                      const horario = ini ? `${ini.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "";
                      const horarioFim = fim ? `${fim.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "";
                      const faixa = horario && horarioFim ? `${horario} as ${horarioFim}` : (horario || "Sem horario");
                      return (
                        <div key={s.id} className="agenda-session-line">
                          <div className="agenda-session-time">{faixa}</div>
                          <div className="agenda-session-body">
                            <div className="agenda-session-title">{cliente} • {info.academiaNome}</div>
                            <div className="agenda-session-meta">
                              {atendimento.competencia || "-"} • {getTipoAtendimentoLabel(atendimento)}
                              {isGlobalTecnico ? ` • ${tecnicoNome}` : ""}
                            </div>
                            <div className="agenda-session-counts">
                              OS: {counts.os} • Preventivas: {counts.preventiva} • {equipamentoLabel}: {counts.equipamento}
                            </div>
                            {acompanhantesNomes.length > 0 && (
                              <div className="agenda-session-counts">
                                Acompanhantes: {acompanhantesNomes.join(", ")}
                              </div>
                            )}
                          </div>
                          <div className="agenda-session-actions">
                            <button
                              type="button"
                              className="agenda-action"
                              onClick={() => abrirModalSessao({ sessao: s })}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="agenda-action"
                              onClick={() => abrirResumoSessao(s)}
                            >
                              Resumo
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="agenda-sem-horario">
              <div className="agenda-sem-horario-titulo">Sem horario</div>
              {pendentesSemHorario.length === 0 && (
                <div className="agend-empty">Nenhuma sessão sem horario.</div>
              )}
              {pendentesSemHorario.map((s) => {
                const atendimento = atendimentosById[s.atendimentoId] || {};
                const cliente = clientesById[atendimento.clienteId]?.nome || "-";
                const info = atendimentosInfo[s.atendimentoId] || { academiaNome: "-", osCount: 0, preventivaCount: 0 };
                const tecnicoNome = tecnicosById[s.tecnicoId]?.nome || "-";
                const counts = sessaoItensTipoCount[s.id] || { os: 0, preventiva: 0, instalacao: 0, equipamento: 0, total: 0 };
                const equipamentoLabel = Number(counts.instalacao || 0) > 0 ? "Instalações" : "Equipamentos";
                const emergencial = emergencialByAtendimento[s.atendimentoId];
                const acompanhantesNomes = Array.isArray(s.tecnicosAcompanhantesNomes)
                  ? s.tecnicosAcompanhantesNomes.filter(Boolean)
                  : [];
                return (
                  <div key={s.id} className="agenda-session-line sem-horario">
                    <div className="agenda-session-time">Sem horario</div>
                    <div className="agenda-session-body">
                      <div className="agenda-session-title">{cliente} • {info.academiaNome}</div>
                      <div className="agenda-session-meta">
                        {atendimento.competencia || "-"} • {getTipoAtendimentoLabel(atendimento)}
                        {isGlobalTecnico ? ` • ${tecnicoNome}` : ""}
                      </div>
                      <div className="agenda-session-counts">
                        OS: {counts.os} • Preventivas: {counts.preventiva} • {equipamentoLabel}: {counts.equipamento}
                      </div>
                      {acompanhantesNomes.length > 0 && (
                        <div className="agenda-session-counts">
                          Acompanhantes: {acompanhantesNomes.join(", ")}
                        </div>
                      )}
                      {emergencial?.disponivel && (
                        <div className="agenda-session-counts" style={{ color: "#f59e0b", fontWeight: 700 }}>
                          ⚡ Emergencial disponível ({emergencial.restantes})
                        </div>
                      )}
                    </div>
                    <div className="agenda-session-actions">
                      <button
                        type="button"
                        className="agenda-action"
                        onClick={() => abrirModalSessao({ sessao: s, atendimento })}
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  const resumoAtendimento = resumoSessao ? atendimentosById[resumoSessao.atendimentoId] || {} : null;
  const resumoCliente = resumoAtendimento ? clientesById[resumoAtendimento.clienteId]?.nome || "-" : "-";
  const resumoItensView = resumoItens.map((item) => {
    const tipo = item?.tipo || (item?.osId ? "OS" : "EQUIPAMENTO");
    if (tipo === "OS") {
      const codigo = item?.osCodigo ? `#${item.osCodigo}` : "OS";
      const titulo = item?.osTitulo || item?.osId || "";
      const label = titulo ? `${codigo} - ${titulo}` : codigo;
      const sub = [item?.setorNome, item?.statusNome].filter(Boolean).join(" • ");
      return { tipo, label, sub, osId: item?.osId };
    }
    if (tipo === "PREVENTIVA" || item?.preventivaId) {
      const codigo = item?.preventivaCodigo || item?.codigo || "";
      const nomePrev = item?.preventivaNome || item?.nome || "Preventiva";
      const label = codigo ? `${codigo} - ${nomePrev}` : nomePrev;
      const sub = [item?.maquinaNome, item?.setorNome, item?.periodicidade].filter(Boolean).join(" • ");
      return { tipo: "PREVENTIVA", label, sub, osId: null, preventivaId: item?.preventivaId };
    }
    const label = item?.equipamentoNome || item?.equipamentoId || "Equipamento";
    const sub = [item?.setorNome, item?.modeloNome].filter(Boolean).join(" • ");
    return { tipo, label, sub, osId: null };
  });

  const body = (
    <div className="agendamentos-container">
      {header}
      <div className="agendamentos-conteudo">
        {leftPanel}
        {rightPanel}
      </div>

      {modalOpen && (
        <div className="agendamentos-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="agendamentos-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modalMode === "edit" ? "Editar sessao" : "Agendar sessao"}</h3>
            {modalIsContrato && (
              <div className="pc-alert pc-alert-info" style={{ marginTop: 12 }}>
                {modalEmergencialContrato
                  ? "Atendimento emergencial por contrato: não é necessário nem permitido calcular distância neste agendamento."
                  : "Contrato ativo: custos (deslocamento, pedágio, mão de obra e peças) não são calculados aqui."}
              </div>
            )}
            <div className="agendamentos-modal-grid">
              <label>
                Tecnico
                <select value={modalTecnicoId} onChange={(e) => setModalTecnicoId(e.target.value)}>
                  <option value="">Selecione</option>
                  {tecnicos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome || t.email || "Tecnico"}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ gridColumn: "1 / -1" }}>
                Técnicos acompanhantes
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {tecnicos
                    .filter((t) => String(t.id) !== String(modalTecnicoId || ""))
                    .map((t) => {
                      const checked = modalTecnicosAcompanhantesIds.includes(t.id);
                      return (
                        <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTecnicoAcompanhante(t.id)}
                          />
                          <span>{t.nome || t.email || "Técnico"}</span>
                        </label>
                      );
                    })}
                </div>
              </label>
              <label>
                Inicio
                <input
                  type="datetime-local"
                  value={modalInicio}
                  min={nowDateTimeLocal()}
                  onChange={(e) => setModalInicio(e.target.value)}
                />
              </label>
              <label>
                Fim
                <input type="datetime-local" value={modalFim} onChange={(e) => setModalFim(e.target.value)} />
              </label>
              <label>
                Status
                <select value={modalStatus} onChange={(e) => setModalStatus(e.target.value)}>
                  {STATUS_SESSAO.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              {modalMode === "edit" && (
                <label style={{ gridColumn: "1 / -1" }}>
                  Motivo do remanejamento
                  <textarea
                    rows={2}
                    value={modalMotivoRemanejamento}
                    onChange={(e) => setModalMotivoRemanejamento(e.target.value)}
                    placeholder="Obrigatório ao alterar data ou técnico"
                    disabled={!modalTemRemanejamento}
                  />
                </label>
              )}
              <label style={{ gridColumn: "1 / -1" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={modalAgendaBloqueada}
                    onChange={(e) => {
                      if (!canGerenciarBloqueio) return;
                      setModalAgendaBloqueada(e.target.checked);
                    }}
                    disabled={!canGerenciarBloqueio}
                  />
                  <span>Congelar agenda desta sessão</span>
                </div>
              </label>
              {modalAgendaBloqueada && (
                <label style={{ gridColumn: "1 / -1" }}>
                  Motivo do congelamento
                  <textarea
                    rows={2}
                    value={modalMotivoBloqueio}
                    onChange={(e) => setModalMotivoBloqueio(e.target.value)}
                    placeholder="Obrigatório para bloquear agenda"
                    disabled={!canGerenciarBloqueio}
                  />
                </label>
              )}
              {!canGerenciarBloqueio && modalAgendaBloqueada && (
                <div className="pc-alert pc-alert-info" style={{ gridColumn: "1 / -1", marginTop: 4 }}>
                  Esta sessão está com agenda congelada. Solicite ao admin/gestor para remanejamento.
                </div>
              )}
              <label style={{ gridColumn: "1 / -1" }}>
                Observacoes
                <textarea rows={3} value={modalObs} onChange={(e) => setModalObs(e.target.value)} />
              </label>
              
              <div className="agendamentos-modal-section" style={{ gridColumn: "1 / -1" }}>
                Endereço e Navegação
              </div>
              
              <label style={{ gridColumn: "1 / -1" }}>
                Endereço de destino (academia/cliente)
                <input
                  type="text"
                  value={modalEnderecoDestino || ""}
                  readOnly
                />
                {!modalEnderecoDestino && (
                  <div style={{ fontSize: 12, color: "#f59e0b", paddingTop: 4 }}>
                    ⚠️ Endereço não cadastrado na academia/cliente
                  </div>
                )}
              </label>
              
              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {!modalEmergencialContrato && (
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCalcularDistancia}
                    disabled={!modalEnderecoDestino || !enderecoOrigem || modalCalculandoDistancia}
                  >
                    {modalCalculandoDistancia ? "Calculando..." : "📍 Calcular distância"}
                  </button>
                )}
                
                {modalEnderecoDestino && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      const destino = modalDestinoCoords || modalEnderecoDestino;
                      const url = gerarUrlGoogleMaps(destino, enderecoOrigem || null);
                      window.open(url, "_blank");
                    }}
                  >
                    🗺️ Abrir no Google Maps
                  </button>
                )}
                
                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={modalIdaVolta}
                    onChange={(e) => setModalIdaVolta(e.target.checked)}
                    style={{ margin: 0, width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 13, cursor: "pointer" }}>Ida e Volta</span>
                </label>
                
                {!enderecoOrigem && (
                  <div style={{ fontSize: 12, color: "#f59e0b", padding: 4 }}>
                    ⚠️ Configure o endereço de origem em Opções
                  </div>
                )}
              </div>

					{!modalIsContrato && (
						<>
						{modalMode === "assign" && !modalDistanciaCalculada && (
								<div
									className="pc-alert pc-alert-warning"
									style={{ gridColumn: "1 / -1", marginTop: 8 }}
								>
									Para atendimentos avulsos, é obrigatório calcular a distância antes de salvar.
								</div>
							)}

						<>
							<div className="agendamentos-modal-section" style={{ gridColumn: "1 / -1" }}>
								Financeiro • Deslocamento
							</div>
							<label>
								Km rodados
								<input
									type="number"
									min="0"
									step="0.1"
									value={modalKmRodados}
									readOnly
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Valor deslocamento (R$)
								<input 
									type="text" 
									value={modalValorDeslocamento} 
									readOnly
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Pedagio (R$)
								<input
									type="number"
									min="0"
									step="0.01"
									value={modalValorPedagio}
									onChange={(e) => setModalValorPedagio(e.target.value)}
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Valor mao de obra (R$)
								<input
									type="number"
									min="0"
									step="0.01"
									value={modalValorMaoObra}
									onChange={(e) => setModalValorMaoObra(e.target.value)}
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Valor pecas (R$)
								<input
									type="number"
									min="0"
									step="0.01"
									value={modalValorPecas}
									onChange={(e) => setModalValorPecas(e.target.value)}
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Desconto (R$)
								<input
									type="number"
									min="0"
									step="0.01"
									value={modalValorDesconto}
									onChange={(e) => setModalValorDesconto(e.target.value)}
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
							<label>
								Valor total (R$)
								<input 
									type="text" 
									value={modalValorTotal} 
									readOnly
									disabled={modalMode === "assign" && !modalDistanciaCalculada}
								/>
							</label>
						</>
                </>
              )}
            </div>
            <div className="agendamentos-modal-actions">
              <button type="button" className="btn" onClick={() => setModalOpen(false)}>
                Cancelar
              </button>
					<button
						type="button"
						className="pc-btn-primario"
						onClick={handleSalvarSessao}
						disabled={
							salvandoSessao ||
							(!modalIsContrato && modalMode === "assign" && !modalDistanciaCalculada)
						}
					>
                {salvandoSessao ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resumoOpen && (
        <div className="agendamentos-modal-overlay" onClick={() => setResumoOpen(false)}>
          <div className="agendamentos-modal agendamentos-resumo" onClick={(e) => e.stopPropagation()}>
            <h3>Resumo da sessao</h3>
            {resumoLoading && <div className="status-texto">Carregando resumo...</div>}
            {!resumoLoading && (
              <>
                <div className="agendamentos-resumo-grid">
                  <div>
                    <span>Cliente</span>
                    <strong>{resumoCliente}</strong>
                  </div>
                  <div>
                    <span>Competencia</span>
                    <strong>{resumoAtendimento?.competencia || "-"}</strong>
                  </div>
                  <div>
                    <span>Periodicidade</span>
                    <strong>{getTipoAtendimentoLabel(resumoAtendimento) || "-"}</strong>
                  </div>
                  <div>
                    <span>Qtd itens</span>
                    <strong>{resumoTotal}</strong>
                  </div>
                </div>
                <div className="agendamentos-resumo-lista">
                  <div className="agendamentos-resumo-lista-head">Itens (primeiros 10)</div>
                  {resumoItensView.length === 0 && (
                    <div className="status-texto">Nenhum item planejado.</div>
                  )}
                  {resumoItensView.length > 0 && (
                    <ul>
                      {resumoItensView.map((it, idx) => (
                        <li key={`${it.label}-${idx}`}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                            <span>{it.label}</span>
                            {it.tipo === "OS" && it.osId && (
                              <Link
                                to={`/ordens-servico/atendimento/${it.osId}`}
                                className="btn"
                                onClick={() => setResumoOpen(false)}
                                style={{ padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                Abrir OS
                              </Link>
                            )}

                            {it.tipo === "PREVENTIVA" && it.preventivaId && (
                              <Link
                                to={`/preventivas/executar/${it.preventivaId}`} state={{ competencia: resumoAtendimento?.competencia || null }}
                                className="btn"
                                onClick={() => setResumoOpen(false)}
                                style={{ padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                Abrir Preventiva
                              </Link>
                            )}
                          </div>
                          {it.sub && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{it.sub}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {resumoTotal > resumoItensView.length && (
                    <div className="agendamentos-resumo-extra">+{resumoTotal - resumoItensView.length} itens</div>
                  )}
                </div>
              </>
            )}
            <div className="agendamentos-modal-actions">
              <button type="button" className="btn" onClick={() => setResumoOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Planejamento"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        {body}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} alterarTema={alterarTema} titulo="Planejamento">
      {body}
    </LayoutDesktop>
  );
}
